import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { Card, Button, Form, Alert, Badge, Row, Col, Modal, Nav, Tab, Spinner } from 'react-bootstrap';
import { FaCode, FaPlay, FaDownload, FaGithub, FaRobot, FaCog, FaTrash, FaGripVertical, FaWrench, FaBrain, FaFlask, FaEdit, FaLock, FaRocket, FaClone, FaStop, FaChevronDown, FaChevronRight, FaPaintBrush, FaPlus } from 'react-icons/fa';
import axios from 'axios';
import DrawingCanvas from './DrawingCanvas';
import WorkflowTester from './WorkflowTester';
import WorkflowExecutionReview from './WorkflowExecutionReview';
import SupervisorCodeEditor from './SupervisorCodeEditor';
import WorkflowFinalizer from './WorkflowFinalizer';
import ConfigEditor from './ConfigEditor';
import WorkflowWizard from './WorkflowWizard';
import EditableAgentMeta from './EditableAgentMeta';
import { useAccessibility } from '../contexts/AccessibilityContext';
import { API_URL } from '../config';

const UnifiedWorkflowCreator = forwardRef(({ onCreateAgent }, ref) => {
  const accessibility = useAccessibility();
  // Design Form State (used by Workflow Wizard for context)
  const [howMade, setHowMade] = useState('');
  const [whatToMake, setWhatToMake] = useState('');

  // Workflow Creator State
  const [activeMode, setActiveMode] = useState('automatic'); // 'automatic', 'manual', or 'deployed'
  const [workflowName, setWorkflowName] = useState('');
  const [currentView, setCurrentView] = useState('configure'); // 'configure', 'review', 'finalize'
  
  // Automatic Mode State (Supervisor)
  const [supervisorInput, setSupervisorInput] = useState('');
  const [isRunning, setIsRunning] = useState(false);
  const [results, setResults] = useState([]);
  const [error, setError] = useState(null);
  const [graphData, setGraphData] = useState(null);
  const [generatedFiles, setGeneratedFiles] = useState([]);
  const [status, setStatus] = useState('');
  const [executionResults, setExecutionResults] = useState(null);

  // Manual Mode State (Graph Builder)
  const [availableAgents, setAvailableAgents] = useState([]);
  const [workflowSteps, setWorkflowSteps] = useState([]);
  const [tagFilter, setTagFilter] = useState(null); // null = show all, string = filter by tag
  const [manualWorkflowInput, setManualWorkflowInput] = useState('');
  const [specificItem, setSpecificItem] = useState('');
  const [extraDetails, setExtraDetails] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  // Input file state for workflow (supports multiple files)
  // Each entry: { id?, filePath, fileName }
  const [inputFiles, setInputFiles] = useState([]);
  const [uploadingInputFile, setUploadingInputFile] = useState(false);
  const [availableInputFiles, setAvailableInputFiles] = useState([]);
  const [showMermaid, setShowMermaid] = useState(true);
  const [mermaidCode, setMermaidCode] = useState('graph TD\n    A[Empty Workflow]');
  const [retryConfig, setRetryConfig] = useState({}); // New: retry configuration for agents
  const [typeCompatibilityWarnings, setTypeCompatibilityWarnings] = useState([]); // Type mismatch warnings
  const [expandedAgents, setExpandedAgents] = useState({});
  const [showWorkflowDrawing, setShowWorkflowDrawing] = useState(false);

  // Enhanced Interface State
  const [showCodeModal, setShowCodeModal] = useState(false);
  const [showSupervisorEditor, setShowSupervisorEditor] = useState(false);
  const [showExecutionReview, setShowExecutionReview] = useState(false);
  const [showWorkflowTester, setShowWorkflowTester] = useState(false);
  const [showFinalizer, setShowFinalizer] = useState(false);
  const [generatedCode, setGeneratedCode] = useState('');
  
  // Config Editor State
  const [showConfigEditor, setShowConfigEditor] = useState(false);
  const [configEditorType, setConfigEditorType] = useState('supervisor');
  const [configEditorAgent, setConfigEditorAgent] = useState(null);
  const [supervisorCode, setSupervisorCode] = useState('');
  const [testInput, setTestInput] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [isTesting, setIsTesting] = useState(false);
  const abortControllerRef = useRef(null);
  
  // Accessibility settings for deployed tools, sourced from the global
  // AccessibilityContext (set via the navbar Accessibility panel). Mapped to the
  // shape WorkflowFinalizer/backend expect (`increasedFontSize` <- `largeFont`).
  const accessibilityNeeds = {
    highContrast: accessibility.highContrast,
    textOnly: accessibility.textOnly,
    increasedFontSize: accessibility.largeFont,
  };

  // Human-in-the-loop: paused workflow state
  const [pausedWorkflow, setPausedWorkflow] = useState(null); // { sessionId, agent, question, remainingAgents }
  const [pausedResponse, setPausedResponse] = useState('');

  // Load supervisor code when in automatic mode
  // Expose methods to parent via ref (for Deployed Tools tab integration)
  useImperativeHandle(ref, () => ({
    loadWorkflowForEditing: (workflow) => {
      setActiveMode('manual');
      setWorkflowName(workflow.name || '');
      setWorkflowSteps(workflow.steps || []);
      setManualWorkflowInput(workflow.input || '');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      setTimeout(() => {
        alert(`Editing "${workflow.name}"\n\nWorkflow ID: ${workflow.id}\n\nWhen you click Save, it will update the existing workflow.`);
      }, 300);
    },
    loadWorkflowForCloning: (workflow) => {
      setActiveMode('manual');
      setWorkflowName(`${workflow.name || 'Untitled'} (Copy)`);
      setWorkflowSteps(workflow.steps || []);
      setManualWorkflowInput(workflow.input || '');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
  }));

  useEffect(() => {
    if (activeMode === 'automatic' && !supervisorCode) {
      loadSupervisorCode();
    }
  }, [activeMode]);

  // Load available agents for manual mode
  useEffect(() => {
    if (activeMode === 'manual') {
      loadAgents();
      loadAvailableInputFiles();
    }
  }, [activeMode]);

  // Generate Mermaid code when workflow changes (including loop config)
  useEffect(() => {
    if (activeMode === 'manual') {
      const code = generateMermaid();
      setMermaidCode(code);
      // Check type compatibility
      const warnings = checkTypeCompatibility(workflowSteps);
      setTypeCompatibilityWarnings(warnings);
    }
  }, [workflowSteps, activeMode, retryConfig]);

  // Render Mermaid diagram when code changes
  useEffect(() => {
    if (activeMode === 'manual' && showMermaid && window.mermaid && mermaidCode) {
      requestAnimationFrame(() => {
        setTimeout(() => {
          try {
            const mermaidElements = document.querySelectorAll('pre.mermaid');
            if (mermaidElements.length === 0) {
              // Container not ready yet, skip silently
              return;
            }
            
            // Check if elements are actually mounted in the DOM
            const mountedElements = Array.from(mermaidElements).filter(el => document.body.contains(el));
            if (mountedElements.length === 0) {
              // Elements exist but not mounted yet, skip silently
              return;
            }
            
            renderMermaid(mountedElements);
          } catch (error) {
            // Silently catch Mermaid rendering errors to avoid breaking the UI
            console.warn('Mermaid rendering skipped:', error.message);
          }
        }, 100);
      });
    }
  }, [mermaidCode, showMermaid, activeMode]);

  const loadSupervisorCode = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/get-supervisor-code`);
      if (response.data.success) {
        setSupervisorCode(response.data.code);
      }
    } catch (error) {
      console.error('Failed to load supervisor code:', error);
    }
  };

  // Helper function to render Mermaid
  const renderMermaid = (elements) => {
    try {
      // Validate elements before processing
      if (!elements || elements.length === 0) {
        return;
      }
      
      // Ensure all elements are still in the DOM
      const validElements = Array.from(elements).filter(el => 
        el && document.body.contains(el)
      );
      
      if (validElements.length === 0) {
        return;
      }
      
      validElements.forEach(element => {
        try {
          element.removeAttribute('data-processed');
          element.innerHTML = '';
          element.style.whiteSpace = 'pre';
          element.textContent = mermaidCode;
        } catch (err) {
          console.warn('Failed to update element:', err.message);
        }
      });
      
      // Run mermaid with error handling
      if (window.mermaid && window.mermaid.run) {
        window.mermaid.run({ nodes: validElements }).catch(err => {
          console.warn('Mermaid run error (non-fatal):', err.message);
        });
      }
    } catch (error) {
      console.warn('Mermaid rendering error (non-fatal):', error.message);
    }
  };

  // Automatic Mode Functions (Supervisor)
  const handleAutomaticSubmit = async (e) => {
    e.preventDefault();
    const input = combinedInput || supervisorInput;
    if (!input.trim()) return;

    setIsRunning(true);
    setError(null);
    setResults([]);
    setGraphData(null);
    setGeneratedFiles([]);
    setStatus('');

    try {
      const response = await fetch(`${API_URL}/api/trigger-supervisor`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('jwt_token') || ''}`,
        },
        body: JSON.stringify({ input: input.trim() })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        
        if (done) break;

        const chunk = decoder.decode(value);
        buffer += chunk;
        
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const jsonStr = line.slice(6);
              if (!jsonStr.trim()) continue;
              
              const data = JSON.parse(jsonStr);
              
              switch (data.type) {
                case 'status':
                  setStatus(data.message);
                  break;
                  
                case 'subgraph_update':
                  setResults(prev => [...prev, data]);
                  break;
                  
                case 'update':
                  setResults(prev => [...prev, data]);
                  break;
                  
                case 'graph':
                  setGraphData(data);
                  break;
                  
                case 'generated_file':
                  if (data.file_type === 'file_too_large' || data.file_type === 'json_too_large') {
                    setGeneratedFiles(prev => [...prev, {
                      filename: data.filename,
                      file_path: data.file_path,
                      type: 'file_too_large',
                      message: `File too large to display. Path: ${data.file_path}`
                    }]);
                  } else {
                    setGeneratedFiles(prev => [...prev, {
                      filename: data.filename,
                      data: data.data,
                      type: data.file_type
                    }]);
                  }
                  break;
                  
                case 'complete':
                  setIsRunning(false);
                  // Store execution results for review using callbacks to get current state
                  setResults(currentResults => {
                    setGeneratedFiles(currentFiles => {
                      setGraphData(currentGraph => {
                        setExecutionResults({
                          results: currentResults,
                          generatedFiles: currentFiles,
                          graphData: currentGraph,
                          input: input,
                          mode: 'automatic',
                          timestamp: new Date().toISOString(),
                          status: 'completed',
                          success: true
                        });
                        return currentGraph;
                      });
                      return currentFiles;
                    });
                    return currentResults;
                  });
                  setCurrentView('review');
                  return;
                  
                case 'error':
                  setError(data.error);
                  setIsRunning(false);
                  return;
              }
            } catch (err) {
              console.error('Error parsing SSE data:', err);
            }
          }
        }
      }
    } catch (err) {
      console.error('Supervisor error:', err);
      setError(err.response?.data?.error || 'Failed to execute supervisor');
      setIsRunning(false);
    }
  };

  // Run supervisor with a specific prompt string (called from WorkflowWizard)
  const handleAutomaticSubmitWithInput = async (input) => {
    if (!input || !input.trim()) return;

    setSupervisorInput(input);
    setIsRunning(true);
    setError(null);
    setResults([]);
    setGraphData(null);
    setGeneratedFiles([]);
    setStatus('');

    try {
      const response = await fetch(`${API_URL}/api/trigger-supervisor`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('jwt_token') || ''}` },
        body: JSON.stringify({ input: input.trim() })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        buffer += chunk;
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith(': keepalive') || !line.trim()) continue;
          if (!line.startsWith('data: ')) continue;
          try {
            const data = JSON.parse(line.slice(6));
            switch (data.type) {
              case 'status':
                setStatus(data.message);
                break;
              case 'subgraph_update':
              case 'update':
                setResults(prev => [...prev, data]);
                break;
              case 'graph':
                setGraphData(data);
                break;
              case 'generated_file':
                if (data.file_type === 'file_too_large' || data.file_type === 'json_too_large') {
                  setGeneratedFiles(prev => [...prev, {
                    filename: data.filename,
                    file_path: data.file_path,
                    type: 'file_too_large',
                    message: `File too large to display. Path: ${data.file_path}`
                  }]);
                } else {
                  setGeneratedFiles(prev => [...prev, {
                    filename: data.filename,
                    data: data.data,
                    type: data.file_type
                  }]);
                }
                break;
              case 'complete':
                setIsRunning(false);
                setResults(currentResults => {
                  setGeneratedFiles(currentFiles => {
                    setGraphData(currentGraph => {
                      setExecutionResults({
                        results: currentResults,
                        generatedFiles: currentFiles,
                        graphData: currentGraph,
                        input: input,
                        mode: 'automatic',
                        timestamp: new Date().toISOString(),
                        status: 'completed',
                        success: true
                      });
                      return currentGraph;
                    });
                    return currentFiles;
                  });
                  return currentResults;
                });
                setCurrentView('review');
                return;
              case 'error':
                setError(data.error);
                setIsRunning(false);
                return;
              default:
                break;
            }
          } catch (parseErr) {
            // Skip malformed lines
          }
        }
      }
    } catch (err) {
      console.error('Supervisor error:', err);
      setError(err.response?.data?.error || 'Failed to execute supervisor');
      setIsRunning(false);
    }
  };

  // Manual Mode Functions (Graph Builder)
  const stopWorkflow = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setIsRunning(false);
    setIsTesting(false);
    setPausedWorkflow(null);
    setStatus('Workflow stopped by user');
    setResults(prev => [...prev, { type: 'status', message: '⛔ Workflow stopped by user' }]);
  };

  const resumeWorkflow = async (userResponse) => {
    if (!pausedWorkflow?.sessionId || !userResponse.trim()) return;
    
    const sessionId = pausedWorkflow.sessionId;
    setPausedWorkflow(null);
    setPausedResponse('');
    setIsRunning(true);
    setIsTesting(true);
    setStatus('Resuming workflow...');
    setResults(prev => [...prev, { 
      type: 'status', 
      message: `▶️ Resuming with your input: "${userResponse.trim().substring(0, 80)}${userResponse.length > 80 ? '...' : ''}"` 
    }]);
    
    const controller = new AbortController();
    abortControllerRef.current = controller;
    
    try {
      const response = await fetch(`${API_URL}/api/workflow/resume/${sessionId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream',
          'Authorization': `Bearer ${localStorage.getItem('jwt_token') || ''}`,
        },
        body: JSON.stringify({ response: userResponse.trim() }),
        signal: controller.signal,
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value);
        buffer += chunk;
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const jsonStr = line.slice(6);
            if (!jsonStr.trim()) continue;
            try {
              const data = JSON.parse(jsonStr);
              console.log('Resume workflow event:', data);
              
              switch (data.type) {
                case 'status':
                  setStatus(data.message);
                  setResults(prev => [...prev, data]);
                  break;
                case 'subgraph_update':
                case 'update':
                  setResults(prev => [...prev, data]);
                  break;
                case 'generated_file':
                  setResults(prev => [...prev, data]);
                  if (data.file_type === 'file_too_large' || data.file_type === 'json_too_large') {
                    setGeneratedFiles(prev => [...prev, {
                      filename: data.filename, file_path: data.file_path,
                      type: 'file_too_large', message: `File too large to display. Path: ${data.file_path}`
                    }]);
                  } else {
                    setGeneratedFiles(prev => [...prev, {
                      filename: data.filename, data: data.data, type: data.file_type,
                      file_path: data.file_path, file_type: data.file_type, size: data.size
                    }]);
                  }
                  break;
                case 'input_request':
                  // Agent requesting more input again
                  setPausedWorkflow({
                    sessionId: data.session_id,
                    agent: data.agent,
                    question: data.question,
                    message: data.message,
                    remainingAgents: data.remaining_agents || [],
                  });
                  setPausedResponse('');
                  setIsRunning(false);
                  setIsTesting(false);
                  setStatus(`⏸️ Paused — "${data.agent}" needs your input`);
                  setResults(prev => [...prev, {
                    type: 'input_request', agent: data.agent,
                    question: data.question, message: data.message,
                  }]);
                  break;
                case 'complete':
                  setIsRunning(false);
                  setIsTesting(false);
                  setPausedWorkflow(null);
                  setStatus('Manual workflow complete!');
                  setResults(prev => [...prev, { type: 'complete', message: 'Workflow complete!' }]);
                  break;
                case 'error':
                  setError(data.error);
                  setIsRunning(false);
                  setIsTesting(false);
                  return;
                default:
                  break;
              }
            } catch (e) {
              console.error('Resume SSE parse error:', e);
            }
          }
        }
      }
    } catch (e) {
      if (e.name === 'AbortError') return;
      console.error('Resume stream error:', e);
      setError('Failed to resume workflow');
      setIsRunning(false);
      setIsTesting(false);
    } finally {
      abortControllerRef.current = null;
    }
  };

  const streamManualExecution = async (input, steps, filePaths = []) => {
    // Create abort controller for this execution
    const controller = new AbortController();
    abortControllerRef.current = controller;
    
    setIsRunning(true);
    setError(null);
    setResults([]);
    setGeneratedFiles([]);
    setStatus('');
    
    // Normalise: filePaths may be a single string (legacy) or array
    const filePathsArray = Array.isArray(filePaths)
      ? filePaths
      : (filePaths ? [filePaths] : []);

    try {
      const response = await fetch(`${API_URL}/api/execute-workflow-test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream',
          'Authorization': `Bearer ${localStorage.getItem('jwt_token') || ''}`,
        },
        body: JSON.stringify({
          workflow_type: 'manual',
          stream: true,
          test_input: { input: input || 'Test input' },
          workflow_config: { nodes: steps },
          input_file_paths: filePathsArray
        }),
        signal: controller.signal
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value);
        buffer += chunk;
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const jsonStr = line.slice(6);
            if (!jsonStr.trim()) continue;
            try {
              const data = JSON.parse(jsonStr);
              console.log('Manual workflow event:', data);
              
              switch (data.type) {
                case 'status':
                  setStatus(data.message);
                  setResults(prev => [...prev, data]);
                  break;
                  
                case 'subgraph_update':
                case 'update':
                  console.log('📊 SSE subgraph_update/update received:', JSON.stringify(data, null, 2));
                  console.log('📊 data.update:', data.update);
                  console.log('📊 data.update?.update_type:', data.update?.update_type);
                  setResults(prev => [...prev, data]);
                  break;
                  
                case 'generated_file':
                  console.log('🎯 [Manual] Generated file event:', data);
                  setResults(prev => {
                    console.log('🎯 [Manual] Adding to results, current:', prev.length);
                    return [...prev, data];
                  });
                  
                  if (data.file_type === 'file_too_large' || data.file_type === 'json_too_large') {
                    setGeneratedFiles(prev => {
                      const newFiles = [...prev, {
                        filename: data.filename,
                        file_path: data.file_path,
                        type: 'file_too_large',
                        message: `File too large to display. Path: ${data.file_path}`
                      }];
                      console.log('🎯 [Manual] Updated generatedFiles (too large):', newFiles.length);
                      return newFiles;
                    });
                  } else {
                    setGeneratedFiles(prev => {
                      const newFiles = [...prev, {
                        filename: data.filename,
                        data: data.data,
                        type: data.file_type,
                        file_path: data.file_path,
                        file_type: data.file_type,
                        size: data.size
                      }];
                      console.log('🎯 [Manual] Updated generatedFiles:', newFiles.length, newFiles);
                      return newFiles;
                    });
                  }
                  break;
                  
                case 'input_request':
                  // Agent is asking for user input — pause the workflow
                  console.log('🔴 Workflow paused — agent requesting input:', data);
                  setPausedWorkflow({
                    sessionId: data.session_id,
                    agent: data.agent,
                    question: data.question,
                    message: data.message,
                    remainingAgents: data.remaining_agents || [],
                  });
                  setPausedResponse('');
                  setIsRunning(false);
                  setIsTesting(false);
                  setStatus(`⏸️ Paused — "${data.agent}" needs your input`);
                  setResults(prev => [...prev, {
                    type: 'input_request',
                    agent: data.agent,
                    question: data.question,
                    message: data.message,
                  }]);
                  break;
                
                case 'complete':
                  setIsRunning(false);
                  setPausedWorkflow(null);
                  setStatus('Manual workflow complete!');
                  setResults(prev => [...prev, { type: 'complete', message: 'Workflow complete!' }]);
                  // Don't switch views - keep workflow visible with results
                  break;
                  
                case 'error':
                  setError(data.error);
                  setIsRunning(false);
                  return;
                  
                default:
                  console.log('Unknown event type:', data.type);
              }
            } catch (e) {
              console.error('Manual SSE parse error:', e);
            }
          }
        }
      }
    } catch (e) {
      if (e.name === 'AbortError') {
        console.log('Manual workflow aborted by user');
        return; // Already handled in stopWorkflow
      }
      console.error('Manual stream error:', e);
      setError('Failed to execute manual workflow');
      setIsRunning(false);
    } finally {
      abortControllerRef.current = null;
    }
  };
  const loadAgents = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${API_URL}/api/load-agents`);
      setAvailableAgents(response.data.agents || response.data || []);
    } catch (err) {
      console.error('Failed to load agents:', err);
      setError('Failed to load agents');
    } finally {
      setIsLoading(false);
    }
  };

  // Load available input files for workflow
  const loadAvailableInputFiles = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/workflow-files`);
      const data = response.data;
      setAvailableInputFiles(data.files || (Array.isArray(data) ? data : []));
    } catch (err) {
      console.error('Failed to load input files:', err);
    }
  };

  // Upload one or more input files for the workflow and add them to the list
  const uploadInputFile = async (file) => {
    setUploadingInputFile(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('description', `Workflow input: ${specificItem || 'general'}`);
      
      const response = await axios.post(`${API_URL}/api/workflow-files/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data.success) {
        const f = response.data.file;
        setInputFiles(prev => {
          // Avoid duplicates by file_path
          if (prev.some(x => x.filePath === f.file_path)) return prev;
          return [...prev, { id: f.id, filePath: f.file_path, fileName: f.filename }];
        });
        await loadAvailableInputFiles();
      }
    } catch (error) {
      alert(`Failed to upload: ${error.response?.data?.error || error.message}`);
    } finally {
      setUploadingInputFile(false);
    }
  };

  const removeInputFile = (index) => setInputFiles(prev => prev.filter((_, i) => i !== index));
  const clearInputFiles = () => setInputFiles([]);

  // Clear input file selection (legacy alias)
  const clearInputFile = () => clearInputFiles();

  const handleUpdateAgentMeta = async (agentName, updates) => {
    try {
      await axios.patch(`${API_URL}/api/manifest/agent-meta/${encodeURIComponent(agentName)}`, updates);
      setAvailableAgents(prev => prev.map(a =>
        a.name === agentName ? { ...a, ...updates } : a
      ));
    } catch (err) {
      console.error('Failed to update agent meta:', err);
    }
  };

  const handleDuplicateAgent = async (agent) => {
    const newName = prompt(`Enter a name for the duplicate agent:`, `${agent.name}_copy`);
    if (!newName || !newName.trim()) {
      return; // User cancelled
    }

    try {
      setIsLoading(true);
      const response = await axios.post(`${API_URL}/api/agents/duplicate`, {
        source_agent: agent.name,
        new_agent_name: newName.trim()
      });

      if (response.data.success) {
        alert(`Agent "${newName}" created successfully!`);
        await loadAgents(); // Reload the agent list
      } else {
        alert(`Failed to duplicate agent: ${response.data.error || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Failed to duplicate agent:', err);
      alert(`Failed to duplicate agent: ${err.response?.data?.error || err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRenameAgent = async (agent) => {
    const newName = prompt('Enter new name for this agent:', agent.name);
    if (!newName || newName.trim() === agent.name) return;
    try {
      const response = await axios.post(
        `${API_URL}/api/manifest/rename-agent/${encodeURIComponent(agent.name)}`,
        { new_name: newName.trim() }
      );
      if (response.data.success) {
        await loadAgents();
      } else {
        alert(response.data.error || 'Rename failed');
      }
    } catch (err) {
      alert(err.response?.data?.error || 'Rename failed');
    }
  };

  const handleReloadAgents = async () => {
    try {
      setIsLoading(true);
      const response = await axios.post(`${API_URL}/api/agents/reload`);
      
      if (response.data.success) {
        setAvailableAgents(response.data.agents || []);
        alert(`✅ Agents reloaded! ${response.data.agent_count} agents loaded.`);
      } else {
        alert(`Failed to reload agents: ${response.data.error || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Failed to reload agents:', err);
      alert(`Failed to reload agents: ${err.response?.data?.error || err.message}`);
    } finally {
      setIsLoading(false);
    }
  };


  const handleEditWorkflow = (workflow) => {
    try {
      console.log('Editing workflow:', workflow);
      
      // Validate workflow object
      if (!workflow || typeof workflow !== 'object') {
        throw new Error('Invalid workflow object');
      }
      
      // Clear any existing errors first
      setError(null);
      
      // Batch all state updates together
      setTimeout(() => {
        try {
          // Populate manual workflow with existing data
          setWorkflowName(workflow.name || '');
          setWorkflowSteps(workflow.steps || []);
          setManualWorkflowInput(workflow.input || '');
          
          // Switch to manual mode
          setActiveMode('manual');
          
          // Scroll to top
          window.scrollTo({ top: 0, behavior: 'smooth' });
          
          // Show a message that they're editing
          setTimeout(() => {
            alert(`Editing "${workflow.name}"\n\nWorkflow ID: ${workflow.id}\n\nWhen you click Save, it will update the existing workflow.`);
          }, 300);
        } catch (innerErr) {
          console.error('Inner edit workflow error:', innerErr);
          setError(`Failed to edit workflow: ${innerErr.message || String(innerErr)}`);
        }
      }, 0);
    } catch (err) {
      console.error('Edit workflow error:', err);
      setError(`Failed to edit workflow: ${err.message || String(err)}`);
    }
  };

  // Check a single source→target edge for type compatibility
  const checkEdgeCompat = (srcAgent, tgtAgent, fromIndex, toIndex) => {
    const outputSchema = srcAgent?.output_schema;
    const inputSchema  = tgtAgent?.input_schema;
    if (!outputSchema || !inputSchema) return null;

    const outType = outputSchema.type || 'any';
    const inType  = inputSchema.type  || 'any';

    if (outType === 'any' || inType === 'any') return null;

    const outBase = outType.replace('[]', '');
    const inBase  = inType.replace('[]', '');

    if (outBase === inBase) {
      if (outBase === 'file') {
        const outExts = (outputSchema.extensions || []).map(e => e.toLowerCase());
        const inExts  = (inputSchema.extensions  || []).map(e => e.toLowerCase());
        if (outExts.length > 0 && inExts.length > 0 && !outExts.some(e => inExts.includes(e))) {
          return {
            fromIndex, toIndex,
            fromAgent: srcAgent.name, toAgent: tgtAgent.name,
            fromType: outType, toType: inType,
            message: `File format mismatch: ${srcAgent.name} outputs ${outExts.join('/')} but ${tgtAgent.name} expects ${inExts.join('/')}`
          };
        }
      }
      return null;
    }
    if ((outBase === 'string' && inBase === 'file') || (outBase === 'file' && inBase === 'string')) return null;
    return {
      fromIndex, toIndex,
      fromAgent: srcAgent.name, toAgent: tgtAgent.name,
      fromType: outType, toType: inType,
      message: `Type mismatch: ${srcAgent.name} outputs ${outType} but ${tgtAgent.name} expects ${inType}`
    };
  };

  // Check type compatibility following actual graph edges (input_from or sequential)
  const checkTypeCompatibility = (steps) => {
    const warnings = [];
    if (!steps || steps.length < 2) return warnings;

    const idToIdx = {};
    steps.forEach((s, i) => { idToIdx[s.id] = i; });

    for (let i = 0; i < steps.length; i++) {
      const step = steps[i];
      if (!step?.agent) continue;

      const inputFrom = step.input_from;
      if (inputFrom && inputFrom.length > 0) {
        // Validate each input_from edge
        for (const srcId of inputFrom) {
          if (srcId === '__input__') continue;
          const srcIdx = idToIdx[srcId];
          if (srcIdx === undefined) {
            warnings.push({
              fromIndex: -1, toIndex: i,
              fromAgent: srcId, toAgent: step.agent.name,
              fromType: '?', toType: '?',
              message: `input_from references unknown step '${srcId}'`
            });
            continue;
          }
          if (srcIdx >= i) {
            warnings.push({
              fromIndex: srcIdx, toIndex: i,
              fromAgent: steps[srcIdx]?.agent?.name || srcId, toAgent: step.agent.name,
              fromType: '?', toType: '?',
              message: `'${srcId}' (position ${srcIdx + 1}) must appear before '${step.agent.name}' (position ${i + 1})`
            });
            continue;
          }
          const w = checkEdgeCompat(steps[srcIdx]?.agent, step.agent, srcIdx, i);
          if (w) warnings.push(w);
        }
      } else if (i > 0) {
        // Default sequential: check previous step
        const w = checkEdgeCompat(steps[i - 1]?.agent, step.agent, i - 1, i);
        if (w) warnings.push(w);
      }
    }
    return warnings;
  };

  const handleDragStart = (e, agent) => {
    e.dataTransfer.setData('application/json', JSON.stringify(agent));
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = (e, stepIndex) => {
    e.preventDefault();
    const agentData = JSON.parse(e.dataTransfer.getData('application/json'));
    
    const newStep = {
      id: `step_${Date.now()}`,
      agent: agentData,
      order: stepIndex,
      input_mapping: {},
      output_mapping: {}
    };

    const updatedSteps = [...workflowSteps];
    updatedSteps.splice(stepIndex, 0, newStep);
    
    updatedSteps.forEach((step, index) => {
      step.order = index;
    });
    
    setWorkflowSteps(updatedSteps);
  };

  const removeStep = (stepIndex) => {
    const updatedSteps = workflowSteps.filter((_, index) => index !== stepIndex);
    updatedSteps.forEach((step, index) => {
      step.order = index;
    });
    setWorkflowSteps(updatedSteps);
  };

  const moveStep = (fromIndex, toIndex) => {
    const updatedSteps = [...workflowSteps];
    const [movedStep] = updatedSteps.splice(fromIndex, 1);
    updatedSteps.splice(toIndex, 0, movedStep);
    
    updatedSteps.forEach((step, index) => {
      step.order = index;
    });
    
    setWorkflowSteps(updatedSteps);
  };

  const addAgentToEnd = (agent) => {
    const newStep = {
      id: `step_${Date.now()}`,
      agent: agent,
      order: workflowSteps.length,
      input_mapping: {},
      output_mapping: {}
    };
    setWorkflowSteps([...workflowSteps, newStep]);
  };

  const generateMermaid = () => {
    if (!workflowSteps || workflowSteps.length === 0) {
      return 'graph TD\n    A[Empty Workflow]';
    }

    const sanitizeNodeName = (name) => {
      if (!name || typeof name !== 'string') return 'unnamed';
      return name.replace(/[^a-zA-Z0-9_]/g, '_').replace(/^[0-9]/, '_$&');
    };

    const sanitizeDisplayText = (text) => {
      if (!text || typeof text !== 'string') return 'Unnamed';
      return text.replace(/"/g, '&quot;').replace(/\n/g, '<br>').replace(/\r/g, '');
    };

    const validSteps = workflowSteps.filter(step => step && step.agent && step.agent.name);
    
    if (validSteps.length === 0) {
      return 'graph TD\n    A[No valid workflow steps]';
    }

    const lines = ['graph TD'];
    lines.push('    start([START])');
    
    validSteps.forEach(step => {
      const nodeName = sanitizeNodeName(step.agent.name);
      const displayText = sanitizeDisplayText(step.agent.display_name || step.agent.name);
      const isAI = step.agent.agentType !== 'deterministic';
      lines.push(`    ${nodeName}["${displayText}${isAI ? ' 🤖' : ' ⚙️'}"]`);
    });
    
    lines.push('    end_node([END])');
    lines.push('');
    
    if (validSteps.length === 1) {
      const nodeName = sanitizeNodeName(validSteps[0].agent.name);
      lines.push(`    start --> ${nodeName}`);
      lines.push(`    ${nodeName} --> end_node`);
    } else {
      const firstNodeName = sanitizeNodeName(validSteps[0].agent.name);
      const lastNodeName = sanitizeNodeName(validSteps[validSteps.length - 1].agent.name);
      
      lines.push(`    start --> ${firstNodeName}`);

      // Build step-id -> node-name lookup for input_from edges
      const idToNode = {};
      validSteps.forEach(s => { idToNode[s.id] = sanitizeNodeName(s.agent.name); });
      
      const incompatiblePairs = new Set(
        (checkTypeCompatibility(validSteps)).map(w => `${w.fromIndex}-${w.toIndex}`)
      );

      const drawnTargets = new Set();

      for (let i = 1; i < validSteps.length; i++) {
        const step = validSteps[i];
        const targetName = sanitizeNodeName(step.agent.name);

        if (step.input_from && step.input_from.length > 0) {
          step.input_from.forEach(sourceId => {
            if (sourceId === '__input__') {
              lines.push(`    start --> ${targetName}`);
            } else {
              const sourceName = idToNode[sourceId];
              if (sourceName) {
                lines.push(`    ${sourceName} --> ${targetName}`);
              }
            }
          });
          drawnTargets.add(i);
        } else {
          // Default sequential edge from previous step
          const prevName = sanitizeNodeName(validSteps[i - 1].agent.name);
          if (incompatiblePairs.has(`${i - 1}-${i}`)) {
            lines.push(`    ${prevName} -->|"⚠️ format mismatch"| ${targetName}`);
          } else {
            lines.push(`    ${prevName} --> ${targetName}`);
          }
        }
      }
      
      lines.push(`    ${lastNodeName} --> end_node`);
    }
    
    // Add dotted lines for loop conditions
    validSteps.forEach(step => {
      const config = retryConfig[step.agent.name];
      if (config?.hasCondition) {
        const fromNode = sanitizeNodeName(step.agent.name);
        const loopTarget = config.loopTarget || step.agent.name;
        const toNode = sanitizeNodeName(loopTarget);
        const conditionLabel = config.condition ? config.condition.replace(/"/g, "'").substring(0, 30) : 'retry';
        const limit = config.recursionLimit || 10;
        
        // Dotted line for loop-back
        lines.push(`    ${fromNode} -.->|"${conditionLabel} (max ${limit})"| ${toNode}`);
        
        // If terminate on failure is set, add dotted line to END
        if (config.terminateOnFailure) {
          lines.push(`    ${fromNode} -.->|"failure → stop"| end_node`);
        }
      }
    });
    
    return lines.join('\n') + '\n';
  };

  // Enhanced Functions
  const handleEditCurrentWorkflow = () => {
    if (activeMode === 'automatic') {
      setShowSupervisorEditor(true);
    } else {
      setCurrentView('configure');
    }
  };

  const handleRetestWorkflow = () => {
    setCurrentView('configure');
    setExecutionResults(null);
  };

  const handleLangGraphSupervisor = async () => {
    if (!workflowName.trim()) {
      setError('Please enter a workflow name first');
      return;
    }

    if (workflowSteps.length === 0) {
      setError('Please add at least one agent to the workflow');
      return;
    }

    try {
      setIsLoading(true);
      setError('');

      // Prepare LangGraph workflow data with conditional edges
      const langGraphWorkflowData = {
        name: workflowName,
        nodes: workflowSteps.map(step => ({
          id: step.agent.name,
          agent: {
            name: step.agent.name,
            displayName: step.agent.display_name || step.agent.name,
            description: step.agent.description || '',
            agentType: step.agent.agentType || 'unknown',
            inputs: step.agent.inputs || [],
            outputs: step.agent.outputs || []
          }
        })),
        // Generate both regular edges and conditional edges
        connections: workflowSteps.map((step, index) => {
          if (index < workflowSteps.length - 1) {
            return {
              source: step.agent.name,
              target: workflowSteps[index + 1].agent.name,
              type: 'regular'
            };
          }
          return null;
        }).filter(conn => conn !== null),
        // Add conditional edges for agents with conditions
        conditional_edges: Object.fromEntries(
          Object.entries(retryConfig)
            .filter(([_, config]) => config.hasCondition)
            .map(([agentName, config]) => [
              agentName,
              {
                condition: config.condition,
                loop_target: config.loopTarget,
                continue_target: config.continueTarget,
                recursion_limit: config.recursionLimit
              }
            ])
        ),
        // Global recursion limit
        recursion_limit: Math.max(
          ...Object.values(retryConfig)
            .filter(config => config.hasCondition)
            .map(config => config.recursionLimit || 10),
          10
        )
      };

      // Save the LangGraph supervisor
      const response = await axios.post(`${API_URL}/api/save-langgraph-supervisor`, langGraphWorkflowData);

      if (response.data.success) {
        const conditionCount = Object.keys(langGraphWorkflowData.conditional_edges).length;
        alert(`🎉 LangGraph supervisor with conditional edges saved successfully!\n\nFeatures enabled:\n• ${conditionCount} conditional loops\n• Recursion limit: ${langGraphWorkflowData.recursion_limit}\n• Native LangGraph patterns\n\nFile: ${response.data.message}`);
        setShowCodeModal(false);
      } else {
        setError(response.data.error || 'Failed to save LangGraph supervisor');
      }
    } catch (err) {
      console.error('Failed to save LangGraph supervisor:', err);
      setError(err.response?.data?.error || err.message || 'Failed to save LangGraph supervisor');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFinalizeWorkflow = () => {
    setShowFinalizer(true);
  };

  const handleSaveSupervisorCode = async (code) => {
    try {
      const response = await axios.post(`${API_URL}/api/save-supervisor-code`, {
        code: code
      });
      
      if (response.data.success) {
        setSupervisorCode(code);
        setShowSupervisorEditor(false);
      } else {
        throw new Error(response.data.error || 'Failed to save supervisor code');
      }
    } catch (error) {
      console.error('Failed to save supervisor code:', error);
      setError('Failed to save supervisor code: ' + error.message);
    }
  };

  const handleTestSupervisorCode = async (code) => {
    try {
      const response = await axios.post(`${API_URL}/api/test-supervisor-code`, {
        code: code,
        input: combinedInput
      });
      
      if (response.data.success) {
        alert('Supervisor code test successful!');
      } else {
        alert('Supervisor code test failed: ' + response.data.error);
      }
    } catch (error) {
      console.error('Failed to test supervisor code:', error);
      alert('Failed to test supervisor code: ' + error.message);
    }
  };

  const handleFinalizeComplete = (deploymentInfo) => {
    setShowFinalizer(false);
    if (deploymentInfo.success) {
      alert(`Design tool deployed successfully!\nURL: ${deploymentInfo.toolUrl}\nYou can now share this with users.`);
      try {
        const url = (deploymentInfo.toolUrl || '').startsWith('http')
          ? deploymentInfo.toolUrl
          : `${API_URL}${deploymentInfo.toolUrl || ''}`;
        window.open(url, '_blank', 'noopener');
      } catch (e) {
        // no-op
      }
      
      // Reset the workflow creator
      setCurrentView('configure');
      setExecutionResults(null);
      setWorkflowName('');
      setWorkflowSteps([]);
      setResults([]);
      setGeneratedFiles([]);
    }
  };

  // Infer generated file list from manual outputs for preview/downloads
  function inferGeneratedFiles(execResponse) {
    try {
      const files = [];
      const results = execResponse?.result?.results || {};
      Object.values(results).forEach((r) => {
        const out = r?.result || r?.output || r;
        if (typeof out === 'string' && /\.(svg|png|jpg|jpeg)$/i.test(out)) {
          files.push({ filename: out.split('/').pop(), type: out.toLowerCase().endsWith('.svg') ? 'image/svg+xml' : 'image/png' });
        }
      });
      return files;
    } catch (e) {
      return [];
    }
  }

  // Combine design form inputs with workflow input
  const combinedInput = React.useMemo(() => {
    if (howMade || whatToMake) {
      let combined = '';
      if (howMade) combined += `Manufacturing method: ${howMade}. `;
      if (whatToMake) combined += `Project description: ${whatToMake}. `;
      if (supervisorInput) combined += `Additional requirements: ${supervisorInput}`;
      return combined.trim();
    }
    return supervisorInput;
  }, [howMade, whatToMake, supervisorInput]);

  const downloadFile = (file) => {
    const backendUrl = API_URL;
    
    if (file.file_path) {
      // Use the server path for download
      const downloadUrl = `${backendUrl}${file.file_path}`;
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = file.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else if (file.data) {
      // Fallback: use base64 data
      let dataUrl;
      if (file.file_type === 'svg' || file.type === 'image/svg+xml') {
        const svgData = typeof file.data === 'string' && !file.data.startsWith('data:') 
          ? file.data 
          : atob(file.data);
        dataUrl = `data:image/svg+xml;base64,${btoa(svgData)}`;
      } else if (file.file_type === 'image') {
        dataUrl = `data:image/png;base64,${file.data}`;
      } else if (file.type && file.type.startsWith('image/')) {
        dataUrl = `data:${file.type};base64,${file.data}`;
      } else {
        dataUrl = `data:application/octet-stream;base64,${file.data}`;
      }
      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = file.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const renderGeneratedFile = (file) => {
    const backendUrl = API_URL;
    const fileUrl = file.file_path ? `${backendUrl}${file.file_path}` : null;
    
    // Handle files that are too large
    if (file.type === 'file_too_large' || file.file_type === 'file_too_large') {
      return (
        <div className="mb-3 p-3 border rounded">
          <div className="d-flex justify-content-between align-items-start mb-2">
            <h6 className="mb-0">{file.filename}</h6>
            <Button size="sm" variant="primary" onClick={() => downloadFile(file)}>
              Download
            </Button>
          </div>
          <Alert variant="warning" className="mb-0">
            <strong>File too large to display</strong><br/>
            {file.message || 'Click download to save the file'}
          </Alert>
        </div>
      );
    }
    
    // Handle SVG files (both new and old format)
    if (file.file_type === 'svg' || file.type === 'image/svg+xml') {
      const svgData = file.file_type === 'svg' ? file.data : atob(file.data);
      return (
        <div className="mb-3 p-3 border rounded">
          <div className="d-flex justify-content-between align-items-start mb-2">
            <h6 className="mb-0">{file.filename}</h6>
            <Button size="sm" variant="primary" onClick={() => downloadFile(file)}>
              Download SVG
            </Button>
          </div>
          <div 
            style={{ 
              border: '1px solid #ddd', 
              padding: '10px', 
              backgroundColor: '#f8f9fa',
              maxHeight: '300px',
              overflow: 'auto'
            }}
            dangerouslySetInnerHTML={{ __html: svgData }}
          />
          {file.size && <small className="text-muted">Size: {(file.size / 1024).toFixed(1)} KB</small>}
        </div>
      );
    } 
    
    // Handle image files (PNG, JPG, etc.)
    if (file.file_type === 'image' || (file.type && file.type.startsWith('image/'))) {
      const imgSrc = file.data && !file.file_path 
        ? `data:${file.type || 'image/png'};base64,${file.data}` 
        : fileUrl;
      return (
        <div className="mb-3 p-3 border rounded">
          <div className="d-flex justify-content-between align-items-start mb-2">
            <h6 className="mb-0">{file.filename}</h6>
            <Button size="sm" variant="primary" onClick={() => downloadFile(file)}>
              Download Image
            </Button>
          </div>
          <img 
            src={imgSrc} 
            alt={file.filename}
            style={{ maxWidth: '100%', maxHeight: '300px', display: 'block' }}
          />
          {file.size && <small className="text-muted">Size: {(file.size / 1024).toFixed(1)} KB</small>}
        </div>
      );
    }
    
    // Handle other file types
    return (
      <div className="mb-3 p-3 border rounded">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h6 className="mb-0">{file.filename}</h6>
            {file.size && <small className="text-muted">Size: {(file.size / 1024).toFixed(1)} KB</small>}
          </div>
          <Button size="sm" variant="primary" onClick={() => downloadFile(file)}>
            Download
          </Button>
        </div>
      </div>
    );
  };

  // Render different views based on currentView state
  const renderCurrentView = () => {
    switch (currentView) {
      case 'review':
        return (
          <WorkflowExecutionReview
            executionData={executionResults}
            onFinalize={handleFinalizeWorkflow}
            onEditWorkflow={handleEditWorkflow}
            onRetestWorkflow={handleRetestWorkflow}
            workflowType={activeMode}
          />
        );
      
      case 'configure':
      default:
        return renderConfigureView();
    }
  };

  const renderConfigureView = () => (
    <>
      {/* Workflow Creator Tabs */}
      <Card>
        <Card.Header style={{ padding: 0, overflow: 'visible', borderRadius: '6px 6px 0 0' }}>
          <Nav variant="tabs" className="mode-tabs">
            <Nav.Item>
              <Nav.Link 
                active={activeMode === 'automatic'} 
                onClick={() => setActiveMode('automatic')}
                className="d-flex align-items-center"
                data-testid="automatic-mode-tab"
                aria-label="Switch to Automatic Mode"
              >
                <FaBrain className="me-2" />
                Automatic (AI Supervision)
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link 
                active={activeMode === 'manual'} 
                onClick={() => setActiveMode('manual')}
                className="d-flex align-items-center"
                data-testid="manual-mode-tab"
                aria-label="Switch to Manual Mode"
              >
                <FaWrench className="me-2" />
                Manual (Agent Orchestration)
              </Nav.Link>
            </Nav.Item>
          </Nav>
        </Card.Header>

        <Card.Body className={activeMode === 'automatic' ? 'automatic-mode' : 'manual-mode'}>
          {activeMode === 'automatic' ? (
            /* Automatic Mode (Supervisor) */
            <div>
              {/* Advanced options */}
              <div className="mb-3 text-end">
                <Button 
                  variant="link" 
                  size="sm" 
                  className="text-muted"
                  onClick={() => {
                    setConfigEditorType('supervisor');
                    setConfigEditorAgent(null);
                    setShowConfigEditor(true);
                  }}
                  title="Edit supervisor prompt and configuration"
                >
                  <FaCog className="me-1" />Configure
                </Button>
                <Button 
                  variant="link" 
                  size="sm" 
                  className="text-muted"
                  onClick={() => setShowSupervisorEditor(true)}
                >
                  <FaEdit className="me-1" />Advanced
                </Button>
              </div>

              {/* Workflow Wizard - conversational planner */}
              <WorkflowWizard
                designContext={{ howMade, whatToMake }}
                onCreateAgent={onCreateAgent}
                onRunSupervisor={(prompt) => {
                  setSupervisorInput(prompt);
                  // Trigger the supervisor run with the wizard's refined prompt
                  setTimeout(() => {
                    const fakeEvent = { preventDefault: () => {} };
                    handleAutomaticSubmitWithInput(prompt);
                  }, 100);
                }}
                onBuildManualWorkflow={async (agents) => {
                  // Load agents first if not already loaded
                  let agentsList = availableAgents;
                  if (agentsList.length === 0) {
                    try {
                      const response = await axios.get(`${API_URL}/api/load-agents`);
                      agentsList = response.data.agents || response.data || [];
                      setAvailableAgents(agentsList);
                    } catch (err) {
                      console.error('Failed to load agents:', err);
                    }
                  }
                  // Switch to manual mode and populate the agent chain
                  setActiveMode('manual');
                  const steps = agents.map(agentName => {
                    const agent = agentsList.find(a => a.name === agentName);
                    return {
                      agent: agent || { name: agentName, description: '' },
                      config: {},
                    };
                  });
                  setWorkflowSteps(steps);
                }}
                onRunExistingWorkflow={(workflowId) => {
                  // Navigate to the deployed tools or trigger the workflow directly
                  window.open(`${API_URL}/api/tools/${workflowId}`, '_blank');
                }}
              />

              {/* Supervisor execution progress - shows when running */}
              {(isRunning || results.length > 0) && (
                <Card className="mb-4 mt-4">
                  <Card.Body>
                    <h6 className="mb-3">
                      {isRunning ? '🔄 Workflow in Progress...' : '✅ Workflow Complete'}
                    </h6>
                    <div 
                      style={{ 
                        fontFamily: 'monospace', 
                        fontSize: '13px',
                        backgroundColor: '#f8f9fa',
                        padding: '12px',
                        borderRadius: '5px',
                        maxHeight: '300px',
                        overflow: 'auto'
                      }}
                    >
                      {results.map((result, index) => {
                        let icon = '•';
                        let message = '';
                        let textOutput = null;
                        
                        if (result.type === 'status') {
                          icon = 'ℹ️';
                          message = result.message || status;
                        } else if (result.type === 'subgraph_update' && result.graph_id) {
                          const updateType = result.update?.update_type;
                          if (updateType === 'start') {
                            icon = '▶️';
                            message = `Starting: ${result.graph_id}`;
                          } else if (updateType === 'nudge') {
                            icon = '🔁';
                            message = `Nudging ${result.graph_id} to execute...`;
                            const content = result.update?.meaningful_content;
                            if (content?.agent_said) {
                              textOutput = content.agent_said;
                            }
                          } else if (updateType === 'completed') {
                            icon = '✅';
                            message = `Completed: ${result.graph_id}`;
                            const content = result.update?.meaningful_content;
                            if (content) {
                              textOutput = content.output || content.result || content.text || content.content;
                              if (textOutput && typeof textOutput === 'object') {
                                textOutput = JSON.stringify(textOutput, null, 2);
                              }
                            }
                          } else {
                            icon = '🔄';
                            message = `Agent: ${result.graph_id}`;
                          }
                        } else if (result.type === 'generated_file') {
                          icon = '✨';
                          message = `Generated: ${result.filename}`;
                        } else if (result.type === 'complete') {
                          icon = '✅';
                          message = 'Workflow complete!';
                        } else if (result.type === 'error') {
                          icon = '❌';
                          message = `Error: ${result.error}`;
                        }
                        
                        return message ? (
                          <div key={index} style={{ marginBottom: textOutput ? '8px' : '4px' }}>
                            <div>{icon} {message}</div>
                            {textOutput && (
                              <div 
                                style={{ 
                                  marginLeft: '24px', 
                                  marginTop: '4px',
                                  padding: '8px',
                                  backgroundColor: result.update?.update_type === 'nudge' ? '#fff3cd' : '#e8f4f8',
                                  borderRadius: '4px',
                                  fontSize: '12px',
                                  fontFamily: 'monospace',
                                  whiteSpace: 'pre-wrap',
                                  wordBreak: 'break-word',
                                  maxHeight: '100px',
                                  overflow: 'auto',
                                  border: result.update?.update_type === 'nudge' ? '1px solid #ffc107' : '1px solid #bee5eb'
                                }}
                              >
                                {result.update?.update_type === 'nudge' ? '💭' : '📝'} {textOutput}
                              </div>
                            )}
                          </div>
                        ) : null;
                      })}
                    </div>
                  </Card.Body>
                </Card>
              )}

              {/* Generated Files - Prominent display */}
              {generatedFiles.length > 0 && (
                <Card className="mb-4">
                  <Card.Body>
                    <h6 className="mb-3">📁 Generated Files ({generatedFiles.length})</h6>
                    <Row>
                      {generatedFiles.map((file, index) => (
                        <Col key={`${file.filename}-${index}`} md={12} lg={6}>
                          {renderGeneratedFile(file)}
                        </Col>
                      ))}
                    </Row>
                  </Card.Body>
                </Card>
              )}
            </div>
          ) : (
            /* Manual Mode (Graph Builder) */
            <div>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                  <h5><FaWrench className="me-2" />Manual Workflow (Agent Orchestration)</h5>
                  <p className="text-muted mb-0">Drag and drop agents to create custom LangGraph workflows</p>
                </div>
                <div>
                  <Button 
                    variant="outline-secondary" 
                    size="sm" 
                    className="me-2"
                    onClick={() => {
                      const saved = JSON.parse(localStorage.getItem('savedWorkflows') || '[]');
                      if (saved.length === 0) {
                        alert('No saved workflows found.');
                        return;
                      }
                      const names = saved.map((w, i) => `${i + 1}. ${w.name} (${w.steps?.length || 0} agents)`).join('\n');
                      const choice = prompt(`Select workflow to load:\n\n${names}\n\nEnter number (1-${saved.length}):`);
                      if (choice) {
                        const idx = parseInt(choice) - 1;
                        if (idx >= 0 && idx < saved.length) {
                          const workflow = saved[idx];
                          setWorkflowName(workflow.name);
                          setWorkflowSteps(workflow.steps || []);
                          setManualWorkflowInput(workflow.input || '');
                          alert(`✅ Loaded "${workflow.name}"`);
                        }
                      }
                    }}
                  >
                    <FaDownload className="me-1" />
                    Load Saved
                  </Button>
                  <Button 
                    variant="outline-info" 
                    size="sm" 
                    className="me-2"
                    onClick={() => setShowWorkflowTester(true)}
                    disabled={workflowSteps.length === 0}
                  >
                    <FaFlask className="me-1" />
                    Advanced Testing
                  </Button>
                  <Button 
                    variant="outline-primary" 
                    size="sm"
                    onClick={() => setShowCodeModal(true)}
                    disabled={workflowSteps.length === 0}
                  >
                    <FaCode className="me-1" />
                    Generate Code
                  </Button>
                  {Object.keys(retryConfig).some(key => retryConfig[key]?.hasCondition) && (
                    <Button 
                      variant="outline-success" 
                      size="sm"
                      className="ms-2"
                      onClick={handleLangGraphSupervisor}
                      disabled={workflowSteps.length === 0}
                    >
                      <FaRocket className="me-1" />
                      Generate with Loops
                    </Button>
                  )}
                </div>
              </div>

              <Row>
                <Col md={4}>
                  <Card className="mb-3">
                    <Card.Header>
                      <div className="d-flex justify-content-between align-items-center">
                        <h2 className="h6 mb-0"><FaCode className="me-2" aria-hidden="true" />Agent Palette</h2>
                        <div style={{ display: 'flex', gap: '5px' }}>
                          <Button 
                            variant="outline-primary" 
                            size="sm"
                            onClick={handleReloadAgents}
                            disabled={isLoading}
                            title="Hot reload agents after editing files"
                          >
                            {isLoading ? '⟳' : '🔄 Reload'}
                          </Button>
                          <Button 
                            variant="outline-secondary" 
                            size="sm"
                            onClick={loadAgents}
                            disabled={isLoading}
                          >
                            {isLoading ? 'Loading...' : 'Refresh'}
                          </Button>
                        </div>
                      </div>
                    </Card.Header>
                    <Card.Body>
                      {/* Tag filter bar (native radio group) */}
                      <fieldset className="border-0 p-0 m-0 d-flex flex-wrap gap-1 mb-2" style={{borderBottom: '1px solid #eee', paddingBottom: '6px'}}>
                        <legend className="visually-hidden">Filter agents by category</legend>
                        <label className="native-chip">
                          <input
                            type="radio"
                            className="native-chip-input"
                            name="agentCategoryFilter"
                            checked={tagFilter === null}
                            onChange={() => setTagFilter(null)}
                          />
                          <span className="native-chip-body" style={{
                            fontSize: '0.68rem', padding: '2px 8px',
                            background: tagFilter === null ? 'var(--bs-dark)' : '#fff',
                            color: tagFilter === null ? '#fff' : '#495057',
                            border: `1px solid ${tagFilter === null ? 'transparent' : '#ced4da'}`,
                          }}>All</span>
                        </label>
                        {[
                          { value: 'accessibility', label: '♿ Accessibility', variant: 'info' },
                          { value: '3D printing', label: '🖨️ 3D Print', variant: 'danger' },
                          { value: 'laser cutting', label: '✂️ Laser', variant: 'warning' },
                          { value: 'embroidery', label: '🧵 Embroidery', variant: 'success' },
                          { value: 'knitting', label: '🧶 Knitting', variant: 'secondary' },
                        ].map(tag => {
                          const checked = tagFilter === tag.value;
                          return (
                            <label key={tag.value} className="native-chip">
                              <input
                                type="radio"
                                className="native-chip-input"
                                name="agentCategoryFilter"
                                checked={checked}
                                onChange={() => setTagFilter(tag.value)}
                              />
                              <span className="native-chip-body" style={{
                                fontSize: '0.68rem', padding: '2px 8px',
                                background: checked ? `var(--bs-${tag.variant})` : '#fff',
                                color: checked ? (tag.variant === 'warning' ? '#000' : '#fff') : '#495057',
                                border: `1px solid ${checked ? 'transparent' : '#ced4da'}`,
                              }}>{tag.label}</span>
                            </label>
                          );
                        })}
                      </fieldset>
                      <div className="agent-palette">
                        {availableAgents
                          .filter(agent => tagFilter === null || (agent.tags && agent.tags.includes(tagFilter)))
                          .map((agent, index) => {
                          const isExpanded = expandedAgents[agent.name] || false;
                          const toggleExpand = (e) => {
                            e.stopPropagation();
                            setExpandedAgents(prev => ({ ...prev, [agent.name]: !prev[agent.name] }));
                          };
                          // Short format type label
                          const ioLabel = (() => {
                            const inp = agent.input_schema;
                            const out = agent.output_schema;
                            if (!inp && !out) return null;
                            const fmtExts = (schema) => {
                              if (!schema) return '?';
                              if (schema.extensions && schema.extensions.length > 0) {
                                return schema.extensions.map(e => `.${e}`).join(', ');
                              }
                              return schema.type || '?';
                            };
                            return `${fmtExts(inp)} → ${fmtExts(out)}`;
                          })();
                          return (
                          <div
                            key={index}
                            className="agent-item"
                          >
                            {/* Compact header row — always visible, draggable */}
                            <div
                              draggable
                              onDragStart={(e) => handleDragStart(e, agent)}
                              data-testid={`agent-item-${agent.name}`}
                              data-agent-name={agent.name}
                              style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', rowGap: '4px', cursor: 'move', gap: '6px' }}
                            >
                              <FaGripVertical aria-hidden="true" style={{ flexShrink: 0, fontSize: '0.7rem', color: '#6c757d' }} />
                              <span
                                onClick={toggleExpand}
                                style={{ cursor: 'pointer', flexShrink: 0, fontSize: '0.65rem', color: '#6c757d' }}
                                title={isExpanded ? 'Collapse' : 'Expand details'}
                              >
                                {isExpanded ? <FaChevronDown /> : <FaChevronRight />}
                              </span>
                              <span style={{ flex: '1 1 6rem', minWidth: 0, fontWeight: 600, fontSize: '0.82rem', lineHeight: 1.2, overflowWrap: 'anywhere', wordBreak: 'break-word', opacity: agent.loadError ? 0.5 : 1 }}
                                title={agent.loadError ? 'Agent not loaded locally (available on server)' : undefined}>
                                {agent.loadError ? '⚠ ' : ''}{agent.display_name || agent.name}
                              </span>
                              {/* Compact IO badge */}
                              {ioLabel && (
                                <span style={{ fontSize: '0.6rem', color: '#495057', whiteSpace: 'nowrap', background: '#f0f0f0', borderRadius: 3, padding: '1px 4px' }}>
                                  {ioLabel}
                                </span>
                              )}
                              {/* Inline action buttons */}
                              <span style={{ display: 'flex', gap: '2px', flexShrink: 0 }}>
                                <Button variant="link" size="sm" className="p-0" style={{ fontSize: '0.65rem', color: '#6c757d' }}
                                  aria-label={`Rename ${agent.display_name || agent.name}`}
                                  onClick={(e) => { e.stopPropagation(); handleRenameAgent(agent); }} title="Rename agent">
                                  <FaEdit aria-hidden="true" />
                                </Button>
                                <Button variant="link" size="sm" className="p-0" style={{ fontSize: '0.65rem', color: '#6c757d' }}
                                  aria-label={`Duplicate ${agent.display_name || agent.name}`}
                                  onClick={(e) => { e.stopPropagation(); handleDuplicateAgent(agent); }} title="Duplicate agent">
                                  <FaClone aria-hidden="true" />
                                </Button>
                                <Button variant="link" size="sm" className="p-0" style={{ fontSize: '0.65rem', color: '#6c757d' }}
                                  aria-label={`Edit ${agent.display_name || agent.name}`}
                                  onClick={(e) => { e.stopPropagation(); setConfigEditorType('agent'); setConfigEditorAgent(agent.name); setShowConfigEditor(true); }} title="Edit agent">
                                  <FaCog aria-hidden="true" />
                                </Button>
                                <Button variant="link" size="sm" className="p-0" style={{ fontSize: '0.65rem', color: '#198754' }}
                                  aria-label={`Add ${agent.display_name || agent.name} to end of workflow`}
                                  onClick={(e) => { e.stopPropagation(); addAgentToEnd(agent); }} title="Add to end of workflow">
                                  <FaPlus aria-hidden="true" />
                                </Button>
                              </span>
                            </div>

                            {/* Expanded details — shown on click */}
                            {isExpanded && (
                              <div style={{ marginTop: '4px', paddingLeft: '20px', fontSize: '0.75rem' }}>
                                <EditableAgentMeta
                                  agentName={agent.name}
                                  description={agent.description}
                                  tags={agent.tags}
                                  onUpdate={handleUpdateAgentMeta}
                                />
                                {/* Agent type */}
                                <div style={{ fontSize: '0.7rem', color: '#555', marginBottom: '3px', marginTop: '3px' }}>
                                  {agent.agentType === 'deterministic'
                                    ? <span>⚙️ Deterministic agent</span>
                                    : <span>🤖 AI agent</span>}
                                </div>
                                {/* IO schema */}
                                {(agent.input_schema || agent.output_schema) && (
                                  <div style={{ fontFamily: 'monospace', fontSize: '0.65rem', color: '#666', background: '#f5f5f5', borderRadius: '3px', padding: '2px 6px', display: 'inline-block', marginBottom: '4px' }}>
                                    {agent.input_schema
                                      ? <><span style={{color: '#0d6efd', fontWeight: 600}}>IN</span>{' '}
                                          {agent.input_schema.extensions && agent.input_schema.extensions.length > 0
                                            ? agent.input_schema.extensions.map(e => `.${e}`).join(', ')
                                            : agent.input_schema.type || 'any'}</>
                                      : <><span style={{color: '#0d6efd', fontWeight: 600}}>IN</span> any</>}
                                    <span style={{margin: '0 4px', color: '#aaa'}}>→</span>
                                    {agent.output_schema
                                      ? <><span style={{color: '#198754', fontWeight: 600}}>OUT</span>{' '}
                                          {agent.output_schema.extensions && agent.output_schema.extensions.length > 0
                                            ? agent.output_schema.extensions.map(e => `.${e}`).join(', ')
                                            : agent.output_schema.type || 'any'}</>
                                      : <><span style={{color: '#198754', fontWeight: 600}}>OUT</span> any</>}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                          );
                        })}
                        {availableAgents.length === 0 && !isLoading && (
                          <div className="text-center text-muted">
                            <p>No agents available</p>
                            <Button variant="outline-primary" size="sm" onClick={loadAgents}>
                              Load Agents
                            </Button>
                          </div>
                        )}
                        {isLoading && (
                          <div className="text-center text-muted">
                            <Spinner animation="border" size="sm" className="me-2" />
                            Loading agents...
                          </div>
                        )}
                      </div>
                    </Card.Body>
                  </Card>
                </Col>

                <Col md={8}>
                  <Card className="mb-3">
                    <Card.Header>
                      <div className="d-flex justify-content-between align-items-center">
                        <h2 className="h6 mb-0"><FaPlay className="me-2" aria-hidden="true" />Workflow Configuration</h2>
                      </div>
                    </Card.Header>
                    <Card.Body>
                      <Form.Group className="mb-3">
                        <Form.Label>Workflow Name</Form.Label>
                        <Form.Control
                          type="text"
                          value={workflowName}
                          onChange={(e) => setWorkflowName(e.target.value)}
                          placeholder="my_workflow"
                        />
                      </Form.Group>

                      {workflowSteps.length > 0 && (
                        <Alert variant="info" className="mb-3 py-2">
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <strong>Workflow Summary:</strong>{' '}
                              {workflowSteps.length} {workflowSteps.length === 1 ? 'agent' : 'agents'},{' '}
                              {workflowSteps.length > 1 ? `${workflowSteps.length - 1} connection${workflowSteps.length > 2 ? 's' : ''}` : '0 connections'}
                              {Object.values(retryConfig).filter(c => c?.hasCondition).length > 0 && (
                                <span className="ms-2">
                                  <Badge bg="warning" style={{fontSize: '0.7rem'}}>
                                    🔄 {Object.values(retryConfig).filter(c => c?.hasCondition).length} loop{Object.values(retryConfig).filter(c => c?.hasCondition).length > 1 ? 's' : ''}
                                  </Badge>
                                </span>
                              )}
                            </div>
                            <div style={{fontSize: '0.8rem'}}>
                              {workflowSteps.map((s, i) => (
                                <Badge key={i} bg="light" text="dark" className="me-1" style={{fontSize: '0.7rem'}}>
                                  {i+1}. {s.agent.display_name || s.agent.name}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        </Alert>
                      )}

                      <div className="workflow-table">
                        {workflowSteps.length === 0 ? (
                          <div 
                            className="drop-zone"
                            onDragOver={handleDragOver}
                            onDrop={(e) => handleDrop(e, 0)}
                          >
                            <p>Drag agents here to start building your workflow</p>
                            <small>Drop agents from the palette on the left</small>
                          </div>
                        ) : (
                          workflowSteps.map((step, index) => {
                            const isDeterministic = step.agent.agentType === 'deterministic';
                            const hasLoop = retryConfig[step.agent.name]?.hasCondition;
                            const hasCustomPrompt = step.step_prompt !== undefined;
                            return (
                            <div key={step.id} className="workflow-step">
                              <div className="step-number">{index + 1}</div>
                              <div className="flex-grow-1" style={{minWidth: 0}}>
                                {/* Header row: name + badge + action buttons */}
                                <div className="d-flex justify-content-between align-items-start">
                                  <div>
                                    <div className="d-flex align-items-center gap-2">
                                      <strong style={{fontSize: '0.95rem'}}>{step.agent.display_name || step.agent.name}</strong>
                                      <span style={{fontSize: '0.7rem', color: '#777'}}>
                                        {step.agent.agentType === 'deterministic' ? '⚙️ deterministic' : '🤖 ai'}
                                      </span>
                                    </div>
                                    <div className="text-muted" style={{fontSize: '0.8rem', lineHeight: 1.3, marginTop: '2px'}}>
                                      {step.agent.description || 'No description'}
                                    </div>
                                  </div>
                                  <div className="d-flex gap-1" style={{flexShrink: 0, marginLeft: '8px'}}>
                                    {index > 0 && (
                                      <Button variant="outline-secondary" size="sm" style={{padding: '2px 6px', fontSize: '0.75rem'}}
                                        onClick={() => moveStep(index, index - 1)} title="Move up">↑</Button>
                                    )}
                                    {index < workflowSteps.length - 1 && (
                                      <Button variant="outline-secondary" size="sm" style={{padding: '2px 6px', fontSize: '0.75rem'}}
                                        onClick={() => moveStep(index, index + 1)} title="Move down">↓</Button>
                                    )}
                                    <Button variant="outline-danger" size="sm" style={{padding: '2px 6px', fontSize: '0.75rem'}}
                                      onClick={() => removeStep(index)} title="Remove"><FaTrash /></Button>
                                  </div>
                                </div>

                                {/* Compact options row — collapsed by default */}
                                <div className="step-options-row mt-2">
                                  <Button
                                    variant={hasLoop ? 'info' : 'outline-secondary'}
                                    size="sm"
                                    onClick={() => {
                                      const newConfig = {...retryConfig};
                                      if (!newConfig[step.agent.name]) {
                                        newConfig[step.agent.name] = {};
                                      }
                                      newConfig[step.agent.name].hasCondition = !newConfig[step.agent.name]?.hasCondition;
                                      if (newConfig[step.agent.name].hasCondition) {
                                        newConfig[step.agent.name].condition = 'state.attempts < 3';
                                        newConfig[step.agent.name].loopTarget = step.agent.name;
                                        newConfig[step.agent.name].continueTarget = 'next';
                                      }
                                      setRetryConfig(newConfig);
                                    }}
                                    style={{fontSize: '0.7rem', padding: '2px 8px'}}
                                  >
                                    {hasLoop ? '🔄 Loop Configured' : '+ Add Loop'}
                                  </Button>
                                  {!isDeterministic && (
                                    <Button
                                      variant={hasCustomPrompt ? 'info' : 'outline-secondary'}
                                      size="sm"
                                      onClick={() => {
                                        const updatedSteps = [...workflowSteps];
                                        if (!updatedSteps[index].step_prompt) {
                                          updatedSteps[index].step_prompt = '';
                                        } else {
                                          delete updatedSteps[index].step_prompt;
                                        }
                                        setWorkflowSteps(updatedSteps);
                                      }}
                                      style={{fontSize: '0.7rem', padding: '2px 8px'}}
                                    >
                                      {hasCustomPrompt ? '✏️ Instructions Set' : '+ Add Instructions'}
                                    </Button>
                                  )}
                                  {isDeterministic && (
                                    <span className="text-muted" style={{fontSize: '0.65rem'}}>⚙️ Deterministic — no custom instructions</span>
                                  )}
                                  {index > 0 && (
                                    <Button
                                      variant={step.input_from && step.input_from.length > 0 ? 'info' : 'outline-secondary'}
                                      size="sm"
                                      onClick={() => {
                                        const updatedSteps = [...workflowSteps];
                                        if (updatedSteps[index].input_from) {
                                          delete updatedSteps[index].input_from;
                                        } else {
                                          updatedSteps[index].input_from = [];
                                        }
                                        setWorkflowSteps(updatedSteps);
                                      }}
                                      style={{fontSize: '0.7rem', padding: '2px 8px'}}
                                    >
                                      {step.input_from && step.input_from.length > 0
                                        ? `🔀 ${step.input_from.length} input${step.input_from.length > 1 ? 's' : ''}`
                                        : '+ Multi-Input'}
                                    </Button>
                                  )}
                                </div>

                                {/* Multi-input selector — pick which previous steps feed into this one */}
                                {index > 0 && step.input_from !== undefined && (
                                  <div className="mt-2 p-2" style={{backgroundColor: '#f5f0ff', borderRadius: '6px', fontSize: '0.82rem', border: '1px solid #d5cce5'}}>
                                    <Form.Group>
                                      <Form.Label style={{fontSize: '0.75rem', fontWeight: 600}}>Receive input from:</Form.Label>
                                      <div style={{display: 'flex', flexWrap: 'wrap', gap: 4}}>
                                        {/* Original text input option */}
                                        {(() => {
                                          const isSelected = (step.input_from || []).includes('__input__');
                                          return (
                                            <Badge
                                              key="__input__"
                                              bg={isSelected ? 'success' : 'light'}
                                              text={isSelected ? 'white' : 'dark'}
                                              style={{
                                                cursor: 'pointer',
                                                fontSize: '0.72rem',
                                                padding: '4px 8px',
                                                border: isSelected ? '1px solid #198754' : '1px solid #ccc',
                                              }}
                                              onClick={() => {
                                                const updatedSteps = [...workflowSteps];
                                                const current = updatedSteps[index].input_from || [];
                                                if (isSelected) {
                                                  updatedSteps[index].input_from = current.filter(id => id !== '__input__');
                                                } else {
                                                  updatedSteps[index].input_from = [...current, '__input__'];
                                                }
                                                setWorkflowSteps(updatedSteps);
                                              }}
                                            >
                                              Original Text Input
                                            </Badge>
                                          );
                                        })()}
                                        {workflowSteps.slice(0, index).map((prevStep) => {
                                          const isSelected = (step.input_from || []).includes(prevStep.id);
                                          return (
                                            <Badge
                                              key={prevStep.id}
                                              bg={isSelected ? 'primary' : 'light'}
                                              text={isSelected ? 'white' : 'dark'}
                                              style={{
                                                cursor: 'pointer',
                                                fontSize: '0.72rem',
                                                padding: '4px 8px',
                                                border: isSelected ? '1px solid #0d6efd' : '1px solid #ccc',
                                              }}
                                              onClick={() => {
                                                const updatedSteps = [...workflowSteps];
                                                const current = updatedSteps[index].input_from || [];
                                                if (isSelected) {
                                                  updatedSteps[index].input_from = current.filter(id => id !== prevStep.id);
                                                } else {
                                                  updatedSteps[index].input_from = [...current, prevStep.id];
                                                }
                                                setWorkflowSteps(updatedSteps);
                                              }}
                                            >
                                              {prevStep.agent.display_name || prevStep.agent.name}
                                            </Badge>
                                          );
                                        })}
                                      </div>
                                      {(!step.input_from || step.input_from.length === 0) && (
                                        <Form.Text style={{fontSize: '0.65rem'}}>
                                          Select one or more previous steps. If none selected, defaults to the step directly above.
                                        </Form.Text>
                                      )}
                                      {step.input_from && step.input_from.length > 1 && (() => {
                                        const fileSources = step.input_from
                                          .filter(id => id !== '__input__')
                                          .map(id => workflowSteps.find(s => s.id === id))
                                          .filter(s => s?.agent?.output_schema?.type === 'file');
                                        if (fileSources.length > 1) {
                                          return (
                                            <div style={{marginTop: 4, fontSize: '0.7rem', color: '#6f42c1'}}>
                                              📁 Receives <strong>{fileSources.length} files</strong> from {fileSources.map(s => s.agent.display_name || s.agent.name).join(' + ')}
                                            </div>
                                          );
                                        }
                                        return null;
                                      })()}
                                    </Form.Group>
                                  </div>
                                )}

                                {/* Expanded loop config — only when active */}
                                {hasLoop && (
                                  <div className="mt-2 p-2" style={{backgroundColor: '#f0f7ff', borderRadius: '6px', fontSize: '0.82rem', border: '1px solid #d0e3f5'}}>
                                    <Form.Group className="mb-2">
                                      <Form.Label style={{fontSize: '0.75rem', fontWeight: 600}}>Loop Condition</Form.Label>
                                      <Form.Control
                                        size="sm"
                                        placeholder="state.attempts < 3 && !state.success"
                                        value={retryConfig[step.agent.name]?.condition || ''}
                                        onChange={(e) => {
                                          const newConfig = {...retryConfig};
                                          newConfig[step.agent.name].condition = e.target.value;
                                          setRetryConfig(newConfig);
                                        }}
                                        style={{fontSize: '0.75rem'}}
                                      />
                                      <Form.Text style={{fontSize: '0.65rem'}}>
                                        When true, loops back. When false, continues.
                                      </Form.Text>
                                    </Form.Group>
                                    
                                    <Row>
                                      <Col xs={6}>
                                        <Form.Group>
                                          <Form.Label style={{fontSize: '0.75rem'}}>Loop back to</Form.Label>
                                          <Form.Select
                                            size="sm"
                                            value={retryConfig[step.agent.name]?.loopTarget || step.agent.name}
                                            onChange={(e) => {
                                              const newConfig = {...retryConfig};
                                              newConfig[step.agent.name].loopTarget = e.target.value;
                                              setRetryConfig(newConfig);
                                            }}
                                          >
                                            <option value={step.agent.name}>This agent ({step.agent.name})</option>
                                            {workflowSteps.filter((_, i) => i < index).map(prevStep => (
                                              <option key={prevStep.agent.name} value={prevStep.agent.name}>
                                                {prevStep.agent.display_name || prevStep.agent.name}
                                              </option>
                                            ))}
                                          </Form.Select>
                                        </Form.Group>
                                      </Col>
                                      <Col xs={6}>
                                        <Form.Group>
                                          <Form.Label style={{fontSize: '0.75rem'}}>Continue to</Form.Label>
                                          <Form.Select
                                            size="sm"
                                            value={retryConfig[step.agent.name]?.continueTarget || 'next'}
                                            onChange={(e) => {
                                              const newConfig = {...retryConfig};
                                              newConfig[step.agent.name].continueTarget = e.target.value;
                                              setRetryConfig(newConfig);
                                            }}
                                          >
                                            <option value="next">Next agent</option>
                                            <option value="__end__">End workflow</option>
                                            {workflowSteps.filter((_, i) => i > index).map(nextStep => (
                                              <option key={nextStep.agent.name} value={nextStep.agent.name}>
                                                {nextStep.agent.display_name || nextStep.agent.name}
                                              </option>
                                            ))}
                                          </Form.Select>
                                        </Form.Group>
                                      </Col>
                                    </Row>
                                    
                                    <Row className="mt-2">
                                      <Col xs={6}>
                                        <Form.Group>
                                          <Form.Label style={{fontSize: '0.75rem'}}>Recursion Limit</Form.Label>
                                          <Form.Select
                                            size="sm"
                                            value={retryConfig[step.agent.name]?.recursionLimit || 10}
                                            onChange={(e) => {
                                              const newConfig = {...retryConfig};
                                              newConfig[step.agent.name].recursionLimit = parseInt(e.target.value);
                                              setRetryConfig(newConfig);
                                            }}
                                          >
                                            <option value={3}>3 attempts</option>
                                            <option value={5}>5 attempts</option>
                                            <option value={10}>10 attempts</option>
                                            <option value={20}>20 attempts</option>
                                            <option value={50}>50 attempts</option>
                                          </Form.Select>
                                        </Form.Group>
                                      </Col>
                                      <Col xs={6} className="d-flex align-items-end pb-1">
                                        <Form.Check
                                          type="switch"
                                          id={`terminate-failure-${step.agent.name}`}
                                          label={<span style={{fontSize: '0.7rem'}}>Terminate on failure</span>}
                                          checked={retryConfig[step.agent.name]?.terminateOnFailure || false}
                                          onChange={(e) => {
                                            const newConfig = {...retryConfig};
                                            newConfig[step.agent.name].terminateOnFailure = e.target.checked;
                                            setRetryConfig(newConfig);
                                          }}
                                        />
                                      </Col>
                                    </Row>
                                  </div>
                                )}

                                {/* Expanded instructions — only when active */}
                                {!isDeterministic && hasCustomPrompt && (
                                  <div className="mt-2 p-2" style={{backgroundColor: '#f0f7ff', borderRadius: '6px', fontSize: '0.82rem', border: '1px solid #d0e3f5'}}>
                                    <Form.Group>
                                      <Form.Control
                                        as="textarea"
                                        rows={2}
                                        size="sm"
                                        placeholder="Custom instructions for this step (e.g. &quot;Generate high-contrast output&quot;)"
                                        value={step.step_prompt || ''}
                                        onChange={(e) => {
                                          const updatedSteps = [...workflowSteps];
                                          updatedSteps[index].step_prompt = e.target.value;
                                          setWorkflowSteps(updatedSteps);
                                        }}
                                        style={{fontSize: '0.75rem'}}
                                      />
                                    </Form.Group>
                                  </div>
                                )}
                              </div>
                            </div>
                            );
                          })
                        )}
                        
                        {workflowSteps.length > 0 && (
                          <div 
                            className="drop-zone"
                            onDragOver={handleDragOver}
                            onDrop={(e) => handleDrop(e, workflowSteps.length)}
                          >
                            <p>Drop agents here to add more steps</p>
                          </div>
                        )}
                      </div>

                      {/* Type Compatibility Warnings */}
                      {typeCompatibilityWarnings.length > 0 && (
                        <Alert variant="danger" className="mt-3" style={{border: '2px solid #dc3545'}}>
                          <Alert.Heading style={{fontSize: '1rem'}}>
                            🚫 Incompatible File Formats — Workflow Will Fail
                          </Alert.Heading>
                          <p className="mb-2" style={{fontSize: '0.9rem'}}>
                            The following agent connections pass incompatible file formats. Insert a conversion agent between them to fix this before running.
                          </p>
                          <ul className="mb-0" style={{fontSize: '0.9rem'}}>
                            {typeCompatibilityWarnings.map((warning, idx) => (
                              <li key={idx} className="mb-1">
                                <strong>{warning.fromAgent}</strong>
                                {' → '}
                                <strong>{warning.toAgent}</strong>
                                <br />
                                <span className="text-danger">{warning.message}</span>
                              </li>
                            ))}
                          </ul>
                        </Alert>
                      )}

                      {workflowSteps.length > 0 && (
                        <div className="mermaid-container mt-3">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <h6 className="mb-0">Workflow Diagram</h6>
                            <Button 
                              variant="link" 
                              size="sm"
                              onClick={() => setShowMermaid(!showMermaid)}
                              className="text-muted p-0"
                            >
                              {showMermaid ? '▼ Collapse' : '▶ Expand'}
                            </Button>
                          </div>
                          {showMermaid && (
                            <pre 
                              className="mermaid" 
                              key={`mermaid-${mermaidCode.length}-${mermaidCode.slice(-80)}`}
                            >
                              {mermaidCode}
                            </pre>
                          )}
                        </div>
                      )}

                      {workflowSteps.length > 0 && (
                        <div className="mt-3">
                          {/* Manual Workflow Input - Two Fields */}
                          <Form.Group className="mb-3">
                            <Form.Label>
                              <FaRobot className="me-2" />
                              Specific Item {inputFiles.length === 0 && <span className="text-danger">*</span>}
                            </Form.Label>
                            <Form.Control
                              type="text"
                              value={specificItem}
                              onChange={(e) => setSpecificItem(e.target.value)}
                              placeholder={inputFiles.length > 0 ? "(optional when files are provided)" : "e.g., heart, butterfly, spider web"}
                              disabled={isTesting}
                            />
                            <Form.Text className="text-muted">
                              {inputFiles.length > 0 ? 'Optional when input files are provided' : 'The main subject you want to create'}
                            </Form.Text>
                          </Form.Group>
                          
                          <Form.Group className="mb-3">
                            <Form.Label>
                              Extra Details <span className="text-muted">(optional)</span>
                            </Form.Label>
                            <Form.Control
                              as="textarea"
                              rows={2}
                              value={extraDetails}
                              onChange={(e) => setExtraDetails(e.target.value)}
                              placeholder="e.g., with zigzag pattern, for medical contexts, anatomically correct"
                              disabled={isTesting}
                            />
                            <Form.Text className="text-muted">
                              Additional context for richer, more detailed results
                            </Form.Text>
                          </Form.Group>
                          
                          {/* Input Files Upload (multiple) */}
                          <Form.Group className="mb-3">
                            <Form.Label>
                              Input Files <span className="text-muted">(optional — you can add several)</span>
                            </Form.Label>
                            <div className="p-2 bg-light rounded">
                              {/* Direct file upload + Draw — multiple allowed */}
                              <div className="mb-2 d-flex align-items-center gap-2">
                                <Form.Control
                                  type="file"
                                  multiple
                                  onChange={async (e) => {
                                    const picked = Array.from(e.target.files);
                                    e.target.value = '';
                                    for (const f of picked) await uploadInputFile(f);
                                  }}
                                  disabled={isTesting || uploadingInputFile}
                                  size="sm"
                                  style={{ flex: 1 }}
                                />
                                <Button
                                  variant="outline-secondary"
                                  size="sm"
                                  onClick={() => setShowWorkflowDrawing(true)}
                                  disabled={isTesting || uploadingInputFile}
                                  title="Draw an input image"
                                >
                                  <FaPaintBrush className="me-1" /> Draw
                                </Button>
                                {uploadingInputFile && (
                                  <Spinner animation="border" size="sm" />
                                )}
                              </div>

                              {/* Or select from previously uploaded files */}
                              {availableInputFiles.length > 0 && (
                                <>
                                  <Form.Label className="small text-muted">Or add a previously uploaded file:</Form.Label>
                                  <Form.Select
                                    value=""
                                    onChange={(e) => {
                                      const fileId = e.target.value;
                                      if (!fileId) return;
                                      const file = availableInputFiles.find(f => f.id === parseInt(fileId));
                                      if (file) {
                                        setInputFiles(prev => {
                                          if (prev.some(x => x.filePath === file.file_path)) return prev;
                                          return [...prev, { id: file.id, filePath: file.file_path, fileName: file.filename }];
                                        });
                                      }
                                      e.target.value = '';
                                    }}
                                    disabled={isTesting}
                                    size="sm"
                                  >
                                    <option value="">— select to add —</option>
                                    {availableInputFiles.map(file => (
                                      <option key={file.id} value={file.id}>
                                        {file.filename} ({file.description || 'no description'})
                                      </option>
                                    ))}
                                  </Form.Select>
                                </>
                              )}

                              {/* File pills */}
                              {inputFiles.length > 0 && (
                                <div className="mt-2 d-flex flex-wrap gap-2 align-items-center">
                                  {inputFiles.map((f, i) => (
                                    <span key={i} className="d-inline-flex align-items-center gap-1 px-2 py-1 rounded"
                                      style={{ background: '#e8f4fd', fontSize: '0.85rem' }}>
                                      📎 {f.fileName}
                                      <button
                                        type="button"
                                        onClick={() => removeInputFile(i)}
                                        disabled={isTesting}
                                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc3545', padding: '0 2px', lineHeight: 1 }}
                                        aria-label={`Remove ${f.fileName}`}
                                      >×</button>
                                    </span>
                                  ))}
                                  <Button
                                    variant="link"
                                    size="sm"
                                    className="text-muted p-0"
                                    onClick={clearInputFiles}
                                    disabled={isTesting}
                                  >Clear all</Button>
                                </div>
                              )}
                            </div>
                            <Form.Text className="text-muted">
                              Upload images, SVGs, STLs or other files to send into the workflow
                            </Form.Text>
                          </Form.Group>
                          
                          <div className="d-flex gap-2">
                            <Button 
                              variant={isTesting ? "danger" : "success"}
                              onClick={async () => {
                                if (isTesting) {
                                  // Stop workflow
                                  stopWorkflow();
                                  return;
                                }
                                // Test manual workflow and show results
                                try {
                                  setIsTesting(true);
                                  const hasText = specificItem.trim();
                                  const hasFiles = inputFiles.length > 0;
                                  let fullInput;
                                  if (hasText && extraDetails.trim()) {
                                    fullInput = `AAA${specificItem.trim()} ${extraDetails.trim()}AAA`;
                                  } else if (hasText) {
                                    fullInput = specificItem.trim();
                                  } else if (hasFiles) {
                                    fullInput = `Process the provided input file(s)`;
                                  } else {
                                    fullInput = 'Test input';
                                  }
                                  await streamManualExecution(fullInput, workflowSteps.map(step => ({
                                    id: step.id || step.agent.name,
                                    agent: step.agent,
                                    ...(step.input_from ? { input_from: step.input_from } : {}),
                                    ...(step.step_prompt ? { step_prompt: step.step_prompt } : {}),
                                  })), inputFiles.map(f => f.filePath));
                                } catch (error) {
                                  setError('Failed to test workflow: ' + error.message);
                                } finally {
                                  setIsTesting(false);
                                }
                              }}
                              disabled={!isTesting && (workflowSteps.length === 0 || (!specificItem.trim() && inputFiles.length === 0) || typeCompatibilityWarnings.length > 0)}
                              title={typeCompatibilityWarnings.length > 0 ? 'Fix file format mismatches above before running' : undefined}
                              data-testid="test-manual-workflow-button"
                              aria-label={isTesting ? "Stop Workflow" : "Test Manual Workflow"}
                              style={{ flex: 1 }}
                            >
                              {isTesting ? (
                                <>
                                  <FaStop className="me-2" />
                                  Stop Workflow
                                </>
                              ) : (
                                <>
                                  <FaPlay className="me-2" />
                                  Test Workflow
                                </>
                              )}
                            </Button>
                            
                            <Button 
                              variant="primary" 
                              onClick={() => {
                                // Build combined input with AAA markers
                                const combinedInput = extraDetails.trim() 
                                  ? `AAA${specificItem.trim()} ${extraDetails.trim()}AAA`
                                  : specificItem.trim();
                                
                                const workflowData = {
                                  name: workflowName || `manual_workflow_${Date.now()}`,
                                  type: 'manual',
                                  steps: workflowSteps,
                                  input: combinedInput,
                                  inputFilePaths: inputFiles.map(f => f.filePath),
                                  inputFileNames: inputFiles.map(f => f.fileName),
                                  created: new Date().toISOString()
                                };
                                
                                // Save to local storage
                                const saved = JSON.parse(localStorage.getItem('savedWorkflows') || '[]');
                                saved.push(workflowData);
                                localStorage.setItem('savedWorkflows', JSON.stringify(saved));
                                
                                alert(`✅ Workflow "${workflowData.name}" saved!\n\n${workflowSteps.length} agents configured.`);
                              }}
                              disabled={workflowSteps.length === 0}
                              title="Save workflow configuration"
                            >
                              <FaDownload className="me-2" />
                              Save
                            </Button>
                            
                            <Button 
                              variant="info" 
                              onClick={() => {
                                setShowFinalizer(true);
                              }}
                              disabled={workflowSteps.length === 0}
                              title="Deploy workflow as standalone tool"
                            >
                              <FaRocket className="me-2" />
                              Deploy
                            </Button>
                          </div>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              {/* Human-in-the-loop: Paused Workflow Input Request */}
              {pausedWorkflow && (
                <Row className="mt-3">
                  <Col>
                    <Card border="warning" className="shadow-sm">
                      <Card.Header className="bg-warning text-dark d-flex align-items-center">
                        <span style={{ fontSize: '1.2rem', marginRight: '8px' }}>⏸️</span>
                        <strong>Workflow Paused — Agent Needs Your Input</strong>
                      </Card.Header>
                      <Card.Body>
                        <Alert variant="info" className="mb-3">
                          <strong>{pausedWorkflow.agent}</strong> is requesting additional information:
                        </Alert>
                        <div 
                          style={{ 
                            backgroundColor: '#f8f9fa', 
                            padding: '12px', 
                            borderRadius: '8px',
                            borderLeft: '4px solid #ffc107',
                            marginBottom: '16px',
                            fontFamily: 'inherit',
                            fontSize: '0.95rem',
                            lineHeight: '1.5',
                            whiteSpace: 'pre-wrap',
                          }}
                        >
                          {pausedWorkflow.question}
                        </div>
                        
                        {pausedWorkflow.remainingAgents.length > 0 && (
                          <div className="mb-3">
                            <small className="text-muted">
                              Remaining agents: {pausedWorkflow.remainingAgents.map((a, i) => (
                                <Badge key={i} bg="light" text="dark" className="me-1">{a}</Badge>
                              ))}
                            </small>
                          </div>
                        )}
                        
                        <Form.Group className="mb-3">
                          <Form.Label><strong>Your response:</strong></Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={3}
                            value={pausedResponse}
                            onChange={(e) => setPausedResponse(e.target.value)}
                            placeholder="Provide the information the agent is asking for..."
                            autoFocus
                          />
                        </Form.Group>
                        
                        <div className="d-flex gap-2">
                          <Button
                            variant="success"
                            onClick={() => resumeWorkflow(pausedResponse)}
                            disabled={!pausedResponse.trim()}
                            style={{ flex: 1 }}
                          >
                            <FaPlay className="me-2" />
                            Resume Workflow
                          </Button>
                          <Button
                            variant="outline-danger"
                            onClick={() => {
                              setPausedWorkflow(null);
                              setPausedResponse('');
                              setResults(prev => [...prev, { type: 'status', message: '⛔ Workflow cancelled by user' }]);
                            }}
                          >
                            Cancel
                          </Button>
                        </div>
                      </Card.Body>
                    </Card>
                  </Col>
                </Row>
              )}

              {/* Manual Workflow Execution Results */}
              {(results.length > 0 || generatedFiles.length > 0) && (() => {
                console.log('🎯 [Manual] Rendering results section. Results:', results.length, 'Files:', generatedFiles.length);
                return (
                <Row className="mt-4">
                  <Col>
                    {/* Event Log */}
                    {results.length > 0 && (
                      <Card className="mb-4">
                        <Card.Body>
                          <h6 className="mb-3">
                            {isRunning ? '🔄 Workflow in Progress...' : '✅ Workflow Complete'}
                          </h6>
                          <div 
                            style={{ 
                              fontFamily: 'monospace', 
                              fontSize: '13px',
                              backgroundColor: '#f8f9fa',
                              padding: '12px',
                              borderRadius: '5px',
                              maxHeight: '300px',
                              overflow: 'auto'
                            }}
                          >
                            {results.map((result, index) => {
                              let icon = '•';
                              let message = '';
                              let textOutput = null;
                              let fileLink = null;
                              
                              if (result.type === 'status') {
                                icon = 'ℹ️';
                                message = result.message || status;
                              } else if (result.type === 'subgraph_update' && result.graph_id) {
                                const updateType = result.update?.update_type;
                                if (updateType === 'start') {
                                  icon = '▶️';
                                  message = `Starting: ${result.graph_id}`;
                                } else if (updateType === 'nudge') {
                                  icon = '🔁';
                                  message = `Nudging ${result.graph_id} to execute...`;
                                  const content = result.update?.meaningful_content;
                                  if (content?.agent_said) {
                                    textOutput = content.agent_said;
                                  }
                                } else if (updateType === 'completed') {
                                  icon = '✅';
                                  message = `Completed: ${result.graph_id}`;
                                  // Extract text output from meaningful_content
                                  const content = result.update?.meaningful_content;
                                  if (content) {
                                    textOutput = content.output || content.result || content.text || content.content;
                                    // Handle object outputs
                                    if (textOutput && typeof textOutput === 'object') {
                                      textOutput = JSON.stringify(textOutput, null, 2);
                                    }
                                  }
                                } else {
                                  icon = '🔄';
                                  message = `Agent: ${result.graph_id}`;
                                }
                              } else if (result.type === 'input_request') {
                                icon = '⏸️';
                                message = `Agent "${result.agent}" is asking for your input`;
                              } else if (result.type === 'generated_file') {
                                icon = '📄';
                                message = `Generated: ${result.filename}`;
                                if (result.file_path) {
                                  fileLink = `${API_URL}${result.file_path}`;
                                }
                              } else if (result.type === 'complete') {
                                icon = '🏁';
                                message = 'Workflow complete!';
                              } else if (result.type === 'error') {
                                icon = '❌';
                                message = `Error: ${result.error}`;
                              }
                              
                              return message ? (
                                <div key={index} style={{ marginBottom: textOutput || fileLink ? '8px' : '4px' }}>
                                  <div>
                                    {icon} {message}
                                    {fileLink && (
                                      <a
                                        href={fileLink}
                                        download={result.filename}
                                        style={{ marginLeft: '8px', fontSize: '11px' }}
                                        className="text-primary"
                                      >
                                        ⬇ Download
                                      </a>
                                    )}
                                  </div>
                                  {textOutput && (
                                    <div 
                                      style={{ 
                                        marginLeft: '24px', 
                                        marginTop: '4px',
                                        padding: '8px',
                                        backgroundColor: result.update?.update_type === 'nudge' ? '#fff3cd' : '#e8f4f8',
                                        borderRadius: '4px',
                                        fontSize: '12px',
                                        fontFamily: 'monospace',
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word',
                                        maxHeight: '150px',
                                        overflow: 'auto',
                                        border: result.update?.update_type === 'nudge' ? '1px solid #ffc107' : '1px solid #bee5eb'
                                      }}
                                    >
                                      {result.update?.update_type === 'nudge' ? '💭' : '📝'} {textOutput}
                                    </div>
                                  )}
                                </div>
                              ) : null;
                            })}
                          </div>
                        </Card.Body>
                      </Card>
                    )}

                    {/* Generated Files */}
                    {generatedFiles.length > 0 && (() => {
                      console.log('🎯 [Manual] Rendering files section:', generatedFiles);
                      return (
                      <Card className="mb-4">
                        <Card.Body>
                          <h6 className="mb-3">📁 Generated Files ({generatedFiles.length})</h6>
                          <Row>
                            {generatedFiles.map((file, index) => {
                              console.log('🎯 [Manual] Rendering file:', file.filename);
                              return (
                              <Col key={`${file.filename}-${index}`} md={12} lg={6}>
                                {renderGeneratedFile(file)}
                              </Col>
                            )})}
                          </Row>
                        </Card.Body>
                      </Card>
                    )})()}
                  </Col>
                </Row>
              )})()}
            </div>
          )}
        </Card.Body>
      </Card>
    </>
  );

  return (
    <div className="unified-workflow-creator">
      <style>{`
        .unified-workflow-creator {
          padding: 20px;
          max-width: 1400px;
          margin: 0 auto;
        }
        
        .mode-tabs {
          flex-wrap: wrap;
        }
        .mode-tabs .nav-link {
          font-weight: bold;
          border-radius: 6px 6px 0 0;
          white-space: nowrap;
          padding: 10px 20px;
          color: #495057;
        }
        .mode-tabs .nav-link:hover:not(.active) {
          color: #212529;
          background-color: #e9ecef;
        }
        .mode-tabs .nav-link.active,
        .mode-tabs .nav-link.active:hover,
        .mode-tabs .nav-link.active:focus {
          background: linear-gradient(135deg, #007bff, #0056b3) !important;
          color: #ffffff !important;
          border-color: #007bff !important;
        }
        
        .automatic-mode {
          background: linear-gradient(135deg, #e3f2fd, #f3e5f5);
          border-radius: 0 0 10px 10px;
          padding: 20px;
        }
        
        .manual-mode {
          background: linear-gradient(135deg, #f3e5f5, #e8f5e8);
          border-radius: 0 0 10px 10px;
          padding: 20px;
        }
        
        .agent-palette {
          background: #f8f9fa;
          border: 1px solid #dee2e6;
          border-radius: 0.375rem;
          padding: 0.5rem;
          max-height: calc(100vh - 280px);
          min-height: 300px;
          overflow-y: auto;
        }
        
        .agent-item {
          background: white;
          border: 1px solid #e8e8e8;
          border-radius: 0.375rem;
          padding: 6px 8px;
          margin-bottom: 4px;
          cursor: grab;
          display: flex;
          flex-direction: column;
          align-items: stretch;
          transition: box-shadow 0.15s, border-color 0.15s;
        }
        
        .agent-item:hover {
          background: #f4f6f8;
          border-color: #c0c7ce;
          box-shadow: 0 1px 3px rgba(0,0,0,0.06);
        }
        
        .agent-item:active {
          cursor: grabbing;
        }

        .agent-item-desc {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          line-height: 1.3;
        }
        
        .workflow-step {
          background: white;
          border: 1px solid #dee2e6;
          border-radius: 0.5rem;
          padding: 1rem;
          margin-bottom: 0.75rem;
          display: flex;
          align-items: flex-start;
          gap: 0.75rem;
          box-shadow: 0 1px 3px rgba(0,0,0,0.05);
        }
        
        .workflow-step:hover {
          box-shadow: 0 2px 6px rgba(0,0,0,0.08);
        }
        
        .step-number {
          background: #007bff;
          color: white;
          border-radius: 50%;
          width: 28px;
          height: 28px;
          min-width: 28px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;
          font-size: 0.85rem;
          margin-top: 2px;
        }

        .step-options-row {
          display: flex;
          gap: 6px;
          flex-wrap: wrap;
          align-items: center;
        }
        
        .drop-zone {
          border: 2px dashed #dee2e6;
          border-radius: 0.375rem;
          padding: 2rem;
          text-align: center;
          color: #6c757d;
          margin: 1rem 0;
        }
        
        .drop-zone.drag-over {
          border-color: #007bff;
          background: #f8f9ff;
        }
        
        .mermaid-container {
          background: white;
          border: 1px solid #dee2e6;
          border-radius: 0.375rem;
          padding: 1rem;
          text-align: center;
          min-height: 200px;
        }
        
        .code-preview {
          background: #f8f9fa;
          border: 1px solid #dee2e6;
          border-radius: 0.375rem;
          padding: 1rem;
          font-family: 'Courier New', monospace;
          font-size: 0.85rem;
          line-height: 1.4;
          max-height: 400px;
          overflow-y: auto;
        }
      `}</style>

      <div className="text-center mb-4">
        <h1 className="h2">🏭 Design Tool Factory</h1>
        <p className="text-muted">Create, test, and deploy accessible design workflows as standalone tools</p>
      </div>

      {error && (
        <Alert variant="danger" onClose={() => setError('')} dismissible>
          {typeof error === 'string' ? error : JSON.stringify(error, null, 2)}
        </Alert>
      )}

      {/* Render current view */}
      {renderCurrentView()}

      {/* Supervisor Code Editor Modal */}
      {showSupervisorEditor && (
        <SupervisorCodeEditor
          onSave={handleSaveSupervisorCode}
          onTest={handleTestSupervisorCode}
          onClose={() => setShowSupervisorEditor(false)}
          initialCode={supervisorCode}
          availableAgents={availableAgents}
          workflowType="automatic"
        />
      )}

      {/* Advanced Workflow Tester */}
      {showWorkflowTester && (
        <WorkflowTester
          workflow={{
            name: workflowName,
            combinedInput: activeMode === 'automatic' ? combinedInput : null,
            steps: workflowSteps
          }}
          workflowType={activeMode}
          onClose={() => setShowWorkflowTester(false)}
        />
      )}

      {/* Workflow Finalizer */}
      {showFinalizer && (() => {
        // Build combined input for deployment
        const combinedInput = extraDetails.trim() 
          ? `AAA${specificItem.trim()} ${extraDetails.trim()}AAA`
          : specificItem.trim();
        
        return (
        <WorkflowFinalizer
          workflowData={{
            name: workflowName,
            type: activeMode,
            steps: workflowSteps,
            input: combinedInput
          }}
          executionResults={executionResults}
          accessibilitySettings={accessibilityNeeds}
          onFinalize={handleFinalizeComplete}
          onClose={() => setShowFinalizer(false)}
        />
        );
      })()}

      {/* Code Modal */}
      {showCodeModal && (
        <Modal show={true} onHide={() => setShowCodeModal(false)} size="xl">
          <Modal.Header closeButton>
            <Modal.Title>
              <FaCode className="me-2" />
              Generated {activeMode === 'automatic' ? 'Automatic' : 'Manual'} Workflow Code
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="code-preview">
              <pre>{activeMode === 'automatic' ? supervisorCode : generateMermaid()}</pre>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowCodeModal(false)}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      {/* Config Editor Modal */}
      <ConfigEditor
        show={showConfigEditor}
        onHide={() => {
          setShowConfigEditor(false);
          setConfigEditorAgent(null);
        }}
        type={configEditorType}
        agentName={configEditorAgent}
      />

      {/* Drawing Canvas Modal */}
      <DrawingCanvas
        show={showWorkflowDrawing}
        onHide={() => setShowWorkflowDrawing(false)}
        onSave={async (file) => {
          await uploadInputFile(file);
        }}
      />
    </div>
  );
});

export default UnifiedWorkflowCreator;
